import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ArrowRight,
  CircleHelp,
  ChevronDown,
  ChevronUp,
  MapPin,
  DollarSign,
  Heart,
  Languages,
  Sparkles,
  Lock,
  Users,
  CheckCircle,
  Share2,
  MessageSquare
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { PlanAvailabilityDialog } from "@/components/PlanAvailabilityDialog";
import { CountryLabel } from "@/components/CountryLabel";
import { CountrySafetySignal } from "@/components/CountrySafetySignal";
import { canUserAccessPlanCheckout } from "@/lib/planCheckoutAccess";
import { getGaPagePath, trackGaEvent } from "@/lib/ga4";
import { getCountryTrustSignal } from "@/lib/countryTrust";
import { getRankedCountryReasonCandidates } from "@/lib/countryReasonProfiles";
import { getCountryPreviewSlugByCode } from "@/lib/countryPreviewContent";
import { getCountryDailyPaceDescription, getPaceDecisionContrast, getPaceLightMention, getPaceSignalState } from "@/lib/dailyPaceSignals";
import { getCountryExploreContent } from "@/lib/countryExploreContent";
import { getCountryCostRangePresentation } from "@/lib/currencyDisplay";
import { getMotivationLabel, normalizeMotivations } from "@/lib/motivationOptions";
import { getCountryRemoteCompatibilityLabel, isRemoteRelevant } from "@/lib/remoteWorkSignals";
import { areReasonIdeasTooSimilar, buildFitFallback, buildTradeoffFallback, formatReasonForSurface, qualifyTradeoffAgainstFit } from "@/lib/reasoningQuality";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RESULTS_PRESENTATION_MODE = "lean-preview";
const LEAN_FREE_RESULTS_METRIC_LABELS = new Set([
  "Lifestyle Fit Score",
  "Cost Index",
]);

const formatPriorityList = (items = []) => {
  if (!items.length) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

const dedupeOrderedLabels = (items = []) => Array.from(new Set((items || []).filter(Boolean)));

const stripTrailingPeriod = (text = "") => (text || "").trim().replace(/[.\s]+$/g, "");
const escapeRegExp = (value = "") => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

const buildDisplayScoreMap = (visibleCards = []) => {
  const countries = (visibleCards || []).map((entry) => entry?.country).filter(Boolean);
  if (!countries.length) return {};

  const rawScores = countries.map((country) => Number(country.score) || 0);
  const maxScore = Math.max(...rawScores);
  const minScore = Math.min(...rawScores);
  const spread = Math.max(maxScore - minScore, 0);
  const scoreMap = {};
  let previousDisplayScore = null;

  countries.forEach((country, index) => {
    const rawScore = Number(country.score) || 0;
    let displayScore;

    if (spread >= 6) {
      displayScore = 84 + (((rawScore - minScore) / spread) * 14);
    } else if (spread >= 2.5) {
      displayScore = 89 + (((rawScore - minScore) / spread) * 9);
    } else {
      displayScore = 97.4 - (index * 1.1);
    }

    displayScore = clampNumber(displayScore, 84, 98.6);
    if (previousDisplayScore !== null) {
      displayScore = Math.min(displayScore, previousDisplayScore - 0.6);
    }

    previousDisplayScore = displayScore;
    scoreMap[country.code || country.name] = `${displayScore.toFixed(displayScore >= 96 ? 1 : 0)}%`;
  });

  return scoreMap;
};

const polishLeanPreviewCopy = (text = "", countryName = "", tone = "reason") => {
  let polished = text.replace(/\s+/g, " ").trim();
  if (!polished) return "";

  if (countryName) {
    const leadingCountryPattern = new RegExp(`^${escapeRegExp(countryName)}\\s+`, "i");
    polished = polished.replace(leadingCountryPattern, tone === "watch" ? "This option " : "It ");
  }

  polished = polished
    .replace(/\bmay feel\b/gi, "can feel")
    .replace(/\bbecomes easier to justify\b/gi, "looks stronger")
    .replace(/\bholds up well\b/gi, "still looks strong")
    .replace(/\bstands out a little more\b/gi, "stands out more clearly")
    .replace(/\s+,/g, ",")
    .replace(/\s+\./g, ".");

  return polished.charAt(0).toUpperCase() + polished.slice(1);
};

const buildResultsMeaningInterpretationCopy = ({
  leadName,
  alternativeName,
  leadInterpretationLabel,
  supportingPriorityLabels,
  secondaryInterpretationLabels,
}) => {
  const supportLabels = dedupeOrderedLabels([
    ...(supportingPriorityLabels || []),
    ...(secondaryInterpretationLabels || []),
  ]).filter((label) => label !== leadInterpretationLabel);

  if (!leadInterpretationLabel && !supportLabels.length) return null;

  const supportTail = supportLabels.length
    ? ` The order still holds once you factor in ${formatPriorityList(supportLabels.slice(0, 2))}.`
    : "";

  if (leadInterpretationLabel && alternativeName) {
    if (supportLabels.length) {
      return `${alternativeName} remains the nearest alternative, but it does not quite overtake the current order.${supportTail}`;
    }
      return `${leadName || "The lead option"} still ranks first on ${leadInterpretationLabel}, while ${alternativeName} remains the closest alternative.`;
  }

  if (leadInterpretationLabel) {
    return `${leadName || "The lead option"} still leads on ${leadInterpretationLabel}.${supportTail}`;
  }

  return `The order still holds once you factor in ${formatPriorityList(supportLabels.slice(0, 2))}.`;
};

const buildResultsMeaningLeadLine = ({
  leadName,
  alternativeName,
  leadInterpretationLabel,
  visiblePracticalFactorLabels,
  fallbackText,
}) => {
  if (leadName && alternativeName && visiblePracticalFactorLabels?.length) {
    return `${leadName} still comes out ahead overall, with ${alternativeName} staying closest. The clearest separation shows up around ${formatPriorityList(visiblePracticalFactorLabels.slice(0, 2))}.`;
  }

  if (leadName && alternativeName && leadInterpretationLabel) {
    return `${leadName} still comes out ahead overall, with ${alternativeName} staying closest. The clearest separation is ${leadInterpretationLabel}.`;
  }

  if (leadName && leadInterpretationLabel) {
    return `${leadName} still comes out ahead overall, mainly because it is stronger on ${leadInterpretationLabel}.`;
  }

  return stripTrailingPeriod(fallbackText || "Treat these as your strongest current shortlist, not a generic ranking.") + ".";
};

const buildResultsMeaningSupportCopy = ({
  omittedHighSalienceLabels,
  familyStatus,
  paceDecisionContrast,
  paceLightMention,
}) => {
  if (omittedHighSalienceLabels?.length) {
    const familyTail = ["family_young", "family_older"].includes(familyStatus) && omittedHighSalienceLabels.includes("education quality")
      ? " for a family-oriented move"
      : "";

    return `Other priorities still shaping the order: ${formatPriorityList(omittedHighSalienceLabels.slice(0, 2))}${familyTail}.`;
  }

  return paceDecisionContrast || paceLightMention || null;
};

const summarizeNaturalLanguageIntro = (text = "") => {
  const cleaned = text.replace(/\s+/g, " ").trim();
  if (!cleaned) return "";

  const firstSentence = cleaned.split(/[.!?]/)[0]?.trim() || cleaned;
  const core = firstSentence
    .replace(/^example:\s*/i, "")
    .replace(/^i\s+(want|would like|need|prefer)\s+/i, "")
    .replace(/^i[’']?m\s+looking\s+for\s+/i, "")
    .replace(/^looking\s+for\s+/i, "")
    .replace(/^my\s+priority\s+is\s+/i, "")
    .trim()
    .replace(/[;,]+$/g, "");

  const chunks = core
    .split(/,|\band\b/gi)
    .map((chunk) => chunk.trim().replace(/^(a|an|the)\s+/i, ""))
    .filter(Boolean);

  const uniqueChunks = [];
  chunks.forEach((chunk) => {
    const normalized = chunk.toLowerCase();
    if (!uniqueChunks.some((item) => item.toLowerCase() === normalized)) {
      uniqueChunks.push(chunk);
    }
  });

  const normalizeChunk = (chunk) => {
    const lowered = chunk.toLowerCase();

    if (/calm|calmer|quiet|peaceful/.test(lowered)) return "a calmer lifestyle";
    if (/healthcare|medical/.test(lowered)) return "good healthcare";
    if (/cost|afford|budget|reasonable living/.test(lowered)) return "manageable living costs";
    if (/residency|visa|pathway|path\b/.test(lowered)) return "a realistic residency pathway";
    if (/coast|coastal|beach|seaside|ocean/.test(lowered)) return "coastal living";
    if (/golf/.test(lowered)) return "access to golf";
    if (/shopping|shops|retail/.test(lowered)) return "shopping convenience";
    if (/internet|connect/.test(lowered)) return "reliable internet";
    if (/community|expat/.test(lowered)) return "community connection";
    if (/safety|stable|stability/.test(lowered)) return "safety and stability";

    const polished = chunk
      .replace(/^to\s+/i, "")
      .replace(/^be\s+near\s+/i, "access to ")
      .replace(/^near\s+/i, "access to ")
      .replace(/\ba\s+golf\s+course\b/i, "golf")
      .replace(/\bthe\s+beach\b/i, "coastal living")
      .trim();

    if (/^[A-Z]/.test(polished)) return polished;
    if (/^(good|strong|reliable|manageable|realistic|coastal|shopping|access|community|safety)/i.test(polished)) {
      return polished;
    }

    return `a ${polished}`.replace(/^a\s+a\s+/i, "a ");
  };

  const selected = uniqueChunks
    .map(normalizeChunk)
    .filter(Boolean)
    .filter((value, index, arr) => arr.findIndex((item) => item.toLowerCase() === value.toLowerCase()) === index)
    .slice(0, 3);

  if (selected.length) {
    return `You emphasized ${formatPriorityList(selected)}.`;
  }

  const fallback = core.length > 140 ? `${core.slice(0, 137).trimEnd()}…` : core;
  return fallback ? `You emphasized ${fallback}.` : "";
};

const normalizeRenderedReason = (text = "") => text
  .toLowerCase()
  .replace(/[^a-z0-9\s]/g, " ")
  .replace(/\s+/g, " ")
  .trim();

const dedupeRenderedReasons = (reasons = []) => {
  const selected = [];
  const normalized = [];

  reasons.forEach((reason) => {
    const normalizedReason = normalizeRenderedReason(reason);
    if (!normalizedReason) return;
    if (normalized.includes(normalizedReason)) return;
    if (selected.some((existingReason) => areReasonIdeasTooSimilar(existingReason, reason))) return;

    normalized.push(normalizedReason);
    selected.push(reason);
  });

  return selected;
};

const PERSONALIZATION_THEME_DEFINITIONS = [
  { key: "calm", label: "a calmer day-to-day lifestyle", pattern: /calm|calmer|quiet|peaceful|slower pace|slower lifestyle/i },
  { key: "healthcare", label: "good healthcare", pattern: /healthcare|medical/i },
  { key: "budget", label: "manageable living costs", pattern: /cost|afford|budget|reasonable living|lower costs?/i },
  { key: "residency", label: "a realistic residency pathway", pattern: /residency|visa|pathway|path\b/i },
  { key: "city", label: "bigger-city energy", pattern: /big[- ]city|city energy|urban|nightlife|vibrant/i },
  { key: "family", label: "family life", pattern: /family|kids|children|schools?/i },
  { key: "remote", label: "remote-work flexibility", pattern: /remote|internet|connectivity|work flexibility/i },
];

const LIFE_REFINER_LABELS = {
  arts_culture: "arts and culture",
  fine_dining: "food and dining quality",
  golf_access: "golf access",
  legalized_cannabis: "cannabis policy fit",
  nightlife_entertainment: "nightlife and entertainment",
  shopping: "shopping convenience",
  sightseeing: "sightseeing access",
  public_transportation: "strong public transportation",
  walkability: "walkable daily life",
  beach_environment: "coastal living",
  outdoor_activities: "outdoor access",
};

const derivePersonalizationThemes = (text = "") => {
  const cleaned = text.toLowerCase();
  return PERSONALIZATION_THEME_DEFINITIONS
    .map(({ key, label, pattern }, index) => ({ key, label, position: cleaned.search(pattern), index }))
    .filter(({ position }) => position !== -1)
    .sort((a, b) => (a.position - b.position) || (a.index - b.index))
    .slice(0, 3)
    .map(({ key, label }) => ({ key, label }));
};

const stableHash = (value = "") => Array.from(String(value)).reduce(
  (acc, char) => ((acc << 5) - acc + char.charCodeAt(0)) >>> 0,
  7
);

const pickStableOption = (options, seed) => {
  if (!options.length) return "";
  return options[stableHash(seed) % options.length];
};

const RELOCATION_BUDGET_DETAILS = {
  under_2000: { label: "Under $2,000/month", minTier: 1, maxTier: 1, minAmount: 0, maxAmount: 1999 },
  "2000_2999": { label: "$2,000–$2,999/month", minTier: 2, maxTier: 2, minAmount: 2000, maxAmount: 2999 },
  "3000_3999": { label: "$3,000–$3,999/month", minTier: 3, maxTier: 3, minAmount: 3000, maxAmount: 3999 },
  "4000_5499": { label: "$4,000–$5,499/month", minTier: 4, maxTier: 4, minAmount: 4000, maxAmount: 5499 },
  "5500_7499": { label: "$5,500–$7,499/month", minTier: 5, maxTier: 5, minAmount: 5500, maxAmount: 7499 },
  "7500_plus": { label: "$7,500+/month", minTier: 6, maxTier: 6, minAmount: 7500, maxAmount: 9500 },
  low: { label: "Under $2,000/month", minTier: 1, maxTier: 1, minAmount: 0, maxAmount: 1999 },
  medium: { label: "$2,000–$3,999/month", minTier: 2, maxTier: 3, minAmount: 2000, maxAmount: 3999 },
  high: { label: "$4,000–$5,499/month", minTier: 4, maxTier: 4, minAmount: 4000, maxAmount: 5499 },
  very_high: { label: "$5,500+/month", minTier: 5, maxTier: 6, minAmount: 5500, maxAmount: 9500 },
};

const getRelocationBudgetDetails = (budgetValue) => {
  if (!budgetValue) return null;
  return RELOCATION_BUDGET_DETAILS[budgetValue] || null;
};

const BUDGET_FIT_TONE_STYLES = {
  Comfortable: "border-emerald-200 bg-emerald-50 text-emerald-800",
  Workable: "border-amber-200 bg-amber-50 text-amber-800",
  "Workable (leaning tight)": "border-yellow-200 bg-yellow-50 text-yellow-800",
  Tight: "border-orange-200 bg-orange-50 text-orange-800",
  Stretch: "border-rose-200 bg-rose-50 text-rose-800",
};

const BUDGET_FIT_PRIORITY = {
  Comfortable: 0,
  Workable: 1,
  "Workable (leaning tight)": 2,
  Tight: 3,
  Stretch: 4,
};

const getCountryTypicalBudgetBand = (countryCode, familyStatus) => {
  const slug = getCountryPreviewSlugByCode(countryCode);
  const content = slug ? getCountryExploreContent(slug) : null;
  const budgetBands = content?.costOverview?.budgetBands || [];
  if (!budgetBands.length) return null;

  const preferredIndex = ["couple", "family_young", "family_older"].includes(familyStatus) ? 1 : 0;
  return {
    ...(budgetBands[preferredIndex] || budgetBands[0]),
    bandIndex: budgetBands[preferredIndex] ? preferredIndex : 0,
  };
};

const parseDisplayedRange = (rangeText = "") => {
  const values = Array.from(rangeText.matchAll(/\d[\d,]*/g), (match) => Number(match[0].replace(/,/g, "")));
  if (!values.length) return null;
  if (values.length === 1) {
    return {
      min: values[0],
      max: /\+/.test(rangeText) ? Math.round(values[0] * 1.25) : values[0],
    };
  }
  return {
    min: values[0],
    max: values[1],
  };
};

const getBudgetFitAssessment = (budgetValue, rangeText = "") => {
  const relocationBudget = getRelocationBudgetDetails(budgetValue);
  const countryRange = parseDisplayedRange(rangeText);
  if (!relocationBudget || !countryRange) {
    return {
      fitLabel: "Workable",
      budgetPressureScore: 999,
      rangePosition: "middle",
      metrics: null,
    };
  }

  const userMin = relocationBudget.minAmount;
  const userMax = relocationBudget.maxAmount;
  const countryMin = countryRange.min;
  const countryMax = countryRange.max;
  const userSpan = Math.max(1, userMax - userMin);
  const countrySpan = Math.max(1, countryMax - countryMin);
  const overlap = Math.max(0, Math.min(userMax, countryMax) - Math.max(userMin, countryMin));
  const aboveBudgetRatio = Math.max(0, countryMax - userMax) / countrySpan;
  const ceilingPosition = (countryMax - userMin) / userSpan;
  const midpointPosition = (((countryMin + countryMax) / 2) - userMin) / userSpan;

  let fitLabel = "Workable";
  if (countryMin >= userMax * 1.08 || aboveBudgetRatio >= 0.8) {
    fitLabel = "Stretch";
  } else if (aboveBudgetRatio >= 0.5 || (countryMin > ((userMin + userMax) / 2) && countryMax > userMax)) {
    fitLabel = "Tight";
  } else if (countryMax > userMax || ceilingPosition >= 0.85) {
    fitLabel = "Workable (leaning tight)";
  } else if (countryMax <= userMin * 0.88) {
    fitLabel = "Comfortable";
  } else if (countryMax <= userMin * 1.02) {
    fitLabel = "Workable";
  } else if (countryMax <= userMax && midpointPosition <= 0.7) {
    fitLabel = "Workable";
  } else if (countryMax <= userMax) {
    fitLabel = "Workable (leaning tight)";
  }

  const rangePosition = countryMax <= userMin * 0.84
    ? "lowest"
    : countryMax <= userMin * 0.96
      ? "middle"
      : countryMax <= userMax
        ? "upper"
        : "edge";

  return {
    fitLabel,
    budgetPressureScore: ceilingPosition + (aboveBudgetRatio * 3) + (midpointPosition * 0.35) - (overlap / countrySpan),
    rangePosition,
    metrics: {
      overlap,
      aboveBudgetRatio,
      ceilingPosition,
      midpointPosition,
    },
  };
};

const buildBudgetFitRows = (countries = [], budgetValue, familyStatus) => {
  const baseRows = countries.slice(0, 4).map((country) => {
    const band = getCountryTypicalBudgetBand(country.code, familyStatus);
    const rangePresentation = band?.range ? getCountryCostRangePresentation(country.code, band.range) : null;
    const range = rangePresentation?.displayRange?.replace(/\s*\/ month/i, "") || "Range varies";
    const fitAssessment = getBudgetFitAssessment(
      budgetValue,
      rangePresentation?.usdRangeText?.replace(/\s*\/ month/i, "") || band?.range || ""
    );

    return {
      code: country.code,
      name: country.name,
      range,
      fitLabel: fitAssessment.fitLabel,
      detail: band?.detail || "",
      budgetPressureScore: fitAssessment.budgetPressureScore,
      rangePosition: fitAssessment.rangePosition,
    };
  });

  return baseRows.sort((a, b) => {
    const rankDelta = (BUDGET_FIT_PRIORITY[a.fitLabel] ?? 99) - (BUDGET_FIT_PRIORITY[b.fitLabel] ?? 99);
    if (rankDelta !== 0) return rankDelta;
    return a.budgetPressureScore - b.budgetPressureScore;
  });
};

const getBudgetExplanationContext = (row = {}) => {
  const detail = (row.detail || "").toLowerCase();
  if (/housing|rent|rental|city-center|central|neighborhood/.test(detail)) return "housing";
  if (/healthcare|insurance|school|tuition|service|private care|transport/.test(detail)) return "service";
  if (/groceries|utilities|daily|everyday|overall|general/.test(detail)) return "general";
  return "balanced";
};

const getBudgetExplanationSeverity = (row = {}) => {
  if (row.fitLabel === "Stretch") {
    if (row.budgetPressureScore >= 52) return "far-over";
    if (row.budgetPressureScore >= 44) return "clear-over";
    return "slight-over";
  }
  if (row.fitLabel === "Tight") {
    return row.budgetPressureScore >= 36 ? "high-tight" : "mid-tight";
  }
  if (row.fitLabel === "Workable (leaning tight)") {
    return row.rangePosition === "edge" ? "edge" : "inside";
  }
  if (row.fitLabel === "Workable") {
    if (row.rangePosition === "edge") return "edge";
    if (row.rangePosition === "middle") return "middle";
    return "balanced";
  }
  if (row.fitLabel === "Comfortable") {
    return row.rangePosition === "lowest" ? "low" : "steady";
  }
  return row.rangePosition || "balanced";
};

const BUDGET_EXPLANATION_TEMPLATES = {
  Comfortable: {
    low: {
      housing: [
        "Still sits clearly below your range, even if your housing choices will matter more here.",
        "Leaves a clear cushion inside your range, even when your housing choices will matter more here.",
      ],
      default: [
        "Leaves the clearest room inside your range.",
        "Sits comfortably below your range, with room for normal cost variation.",
      ],
    },
    steady: {
      housing: [
        "Stays comfortably inside your range, though your housing choices will matter more here.",
        "Still fits with room to spare, even if your housing choices will matter more here.",
      ],
      default: [
        "Stays comfortably inside your range.",
        "Fits without putting immediate pressure on the move.",
      ],
    },
  },
  Workable: {
    middle: {
      housing: [
        "Fits within range, though your housing choices will matter more here.",
        "Still fits, even if your housing choices will matter more here.",
      ],
      default: [
        "Fits within range with a workable cushion.",
        "Sits inside your range without immediate budget pressure.",
      ],
    },
    edge: {
      service: [
        "Still fits, though the higher-cost extras narrow the margin here.",
        "Fits within range, but the add-on costs leave less room than the easier options.",
      ],
      default: [
        "Still fits, but the margin is slimmer than in the easier options.",
        "Fits within range, though there is less room for extra costs here.",
      ],
    },
    balanced: {
      default: [
        "Fits within range with typical day-to-day costs still worth watching.",
        "Sits inside your range with a workable overall margin.",
      ],
    },
  },
  "Workable (leaning tight)": {
    edge: {
      housing: [
        "Still fits, but your housing choices will matter more here.",
        "Fits near the top of your range, and your housing choices will matter more here.",
      ],
      service: [
        "Still fits, but the higher-cost extras need closer watching here.",
        "Fits on paper, though service-heavy costs tighten the margin.",
      ],
      default: [
        "Still fits, but there is less room to waste here.",
        "Fits near the upper edge of your range, so choices matter more.",
      ],
    },
    inside: {
      housing: [
        "Still fits, though your housing choices will matter more here.",
        "Fits within range, but your housing choices will matter more here.",
      ],
      default: [
        "Still fits, but the upper end of typical costs matters more here.",
        "Fits within range, though comfort will depend more on day-to-day choices.",
      ],
    },
  },
  Tight: {
    "high-tight": {
      housing: [
        "Runs above much of your range, and your housing choices will matter more here.",
        "Looks tight, and your housing choices will matter more here.",
      ],
      service: [
        "Runs tight once higher service costs are layered in.",
        "Sits above much of your range, especially after the extra service costs are added.",
      ],
      default: [
        "Runs above much of your range, so comfort depends on tighter choices.",
        "Looks tight enough that cost control will shape how practical this feels.",
      ],
    },
    "mid-tight": {
      housing: [
        "Runs tight, and your housing choices will matter more here.",
        "Looks manageable only if your housing choices stay on the lighter side here.",
      ],
      default: [
        "Runs tight, so everyday choices will matter more here.",
        "Sits just above your range, making comfort harder to protect.",
      ],
    },
  },
  Stretch: {
    "far-over": {
      housing: [
        "Sits well beyond your range, and your housing choices will matter more here.",
        "Runs well above your range, and your housing choices will matter more here.",
      ],
      service: [
        "Sits well beyond your range, and the more service-heavy setup makes it harder to keep realistic.",
        "Runs well above your range once the higher-cost services are layered in.",
      ],
      default: [
        "Sits well beyond your range, making this a harder fit without major adjustments.",
        "Runs clearly beyond your range, so a comfortable setup would need major tradeoffs.",
      ],
    },
    "clear-over": {
      housing: [
        "Runs above your range, and your housing choices will matter more here.",
        "Pushes past your budget, and your housing choices will matter more here.",
      ],
      default: [
        "Runs above your range, so staying comfortable will require careful tradeoffs.",
        "Pushes past your budget, making this a harder fit without some clear adjustments.",
      ],
    },
    "slight-over": {
      housing: [
        "Pushes past your budget, and your housing choices will matter more here.",
        "Runs a bit above your range, and your housing choices will matter more here.",
      ],
      default: [
        "Pushes past your budget, so staying comfortable will need some tradeoffs.",
        "Runs a bit above your range, making this workable only with tighter choices.",
      ],
    },
  },
};

const pickBudgetExplanationVariation = (templates = [], occurrence = 0) => {
  if (!templates.length) return "";
  return templates[Math.min(occurrence, templates.length - 1)];
};

const getBudgetExplanationAngleOptions = (row) => {
  const context = getBudgetExplanationContext(row);
  const options = [];
  const pushOption = (value) => {
    if (value && !options.includes(value)) options.push(value);
  };

  pushOption(context);

  if (row.fitLabel === "Stretch") {
    pushOption("realism");
    pushOption("tradeoffs");
    pushOption("location");
  } else if (row.fitLabel === "Tight") {
    pushOption("tradeoffs");
    pushOption("location");
  } else if (row.fitLabel === "Workable (leaning tight)") {
    pushOption("location");
    pushOption("cushion");
  } else {
    pushOption("cushion");
    pushOption("location");
  }

  pushOption("general");
  return options;
};

const assignBudgetExplanationAngles = (rows = []) => {
  const usedAngles = new Set();
  return rows.reduce((acc, row) => {
    const angle = getBudgetExplanationAngleOptions(row).find((option) => !usedAngles.has(option)) || getBudgetExplanationAngleOptions(row)[0] || "general";
    usedAngles.add(angle);
    acc[row.code] = angle;
    return acc;
  }, {});
};

const buildBudgetAngleSpecificExplanation = (row, angle, occurrence) => {
  const severity = getBudgetExplanationSeverity(row);

  if (angle === "location") {
    if (row.fitLabel === "Stretch") {
      return pickBudgetExplanationVariation([
        "Pushes past your range once you move into the pricier parts of the market.",
        "Looks more realistic only if you stay disciplined about where you base yourself.",
      ], occurrence);
    }
    if (row.fitLabel === "Tight") {
      return pickBudgetExplanationVariation([
        "Looks tighter in the more expensive neighborhoods, so location choice matters more here.",
        "Still gets tighter quickly once you move beyond the cheaper setups.",
      ], occurrence);
    }
    if (row.fitLabel === "Workable (leaning tight)") {
      return pickBudgetExplanationVariation([
        "Still fits, but the wrong city or neighborhood would tighten the margin quickly.",
        "Works best if you stay selective about where you actually set up.",
      ], occurrence);
    }
    return pickBudgetExplanationVariation([
      "Still fits comfortably, though the pricier locations will narrow the cushion faster.",
      "Keeps a workable cushion as long as you do not drift into the most expensive setups.",
    ], occurrence);
  }

  if (angle === "tradeoffs") {
    if (row.fitLabel === "Stretch") {
      return pickBudgetExplanationVariation([
        "Runs above your range, so staying comfortable will require careful tradeoffs.",
        "Pushes past your budget, so a comfortable setup would need sharper compromises elsewhere.",
      ], occurrence);
    }
    return pickBudgetExplanationVariation([
      "Runs tight enough that everyday tradeoffs will matter more here.",
      "Leaves less room for comfort choices, so the budget has to stay more disciplined.",
    ], occurrence);
  }

  if (angle === "realism") {
    return pickBudgetExplanationVariation([
      "Sits well beyond your range, making this a harder fit without major adjustments.",
      "Runs far enough above your range that this looks harder to keep realistic day to day.",
    ], occurrence);
  }

  if (angle === "cushion") {
    if (row.fitLabel === "Workable (leaning tight)") {
      return pickBudgetExplanationVariation([
        "Still fits, but the margin is thinner here than it is in the easier options.",
        "Works inside your range, though there is less room to absorb extra costs.",
      ], occurrence);
    }
    return pickBudgetExplanationVariation([
      "Leaves a workable cushion inside your range.",
      "Still sits inside your range with some room to absorb normal cost variation.",
    ], occurrence);
  }

  const context = angle === "balanced" ? "default" : angle;
  const templateGroup = BUDGET_EXPLANATION_TEMPLATES[row.fitLabel]?.[severity] || {};
  const templates = templateGroup[context] || templateGroup.default || [];
  return pickBudgetExplanationVariation(templates, occurrence);
};

const buildBudgetExplanationText = (row, occurrenceByKey, assignedAngle) => {
  const severity = getBudgetExplanationSeverity(row);
  const templateKey = `${row.fitLabel}:${severity}:${assignedAngle}`;
  const occurrence = occurrenceByKey.get(templateKey) || 0;
  occurrenceByKey.set(templateKey, occurrence + 1);
  return buildBudgetAngleSpecificExplanation(row, assignedAngle, occurrence);
};

const getBudgetExplanationUniquenessTail = (row, angle) => {
  const context = angle || getBudgetExplanationContext(row);
  if (row.fitLabel === "Stretch") {
    return context === "housing"
      ? "Runs above your range, and your housing choices will matter more here."
      : context === "service"
        ? "Runs above your range once the higher-cost extras are included."
        : "Runs above your range, so comfort would depend on sharper tradeoffs."
  }

  if (row.fitLabel === "Tight") {
    return context === "housing"
      ? "Looks tight, and your housing choices will matter more here."
      : "Looks tight enough that location and day-to-day choices will matter more."
  }

  if (row.fitLabel === "Workable (leaning tight)") {
    return context === "housing"
      ? "Still fits, but housing narrows the margin faster here."
      : "Still fits, though there is less room to waste here."
  }

  return context === "housing"
    ? "Still fits inside your range, though your housing choices will matter more here."
    : "Still sits inside your range with a workable cushion.";
};

const dedupeBudgetExplanationText = (text, row, seenTexts, angle) => {
  if (!text || !seenTexts.has(text)) {
    return text;
  }

  const fallback = getBudgetExplanationUniquenessTail(row, angle);
  if (!seenTexts.has(fallback)) {
    return fallback;
  }

  const detailTail = row.budgetPressureScore >= 45
    ? " This looks like a harder fit without bigger adjustments."
    : row.rangePosition === "edge"
      ? " This sits closer to the edge of your range than the others here."
      : " This still needs tighter location choices than the easier options.";

  return `${fallback.replace(/\.$/, "")}${detailTail}`;
};

const buildBudgetFitInterpretationBullets = (rows = []) => {
  const occurrenceByKey = new Map();
  const seenTexts = new Set();
  const assignedAngles = assignBudgetExplanationAngles(rows);

  return rows.map((row) => {
    const assignedAngle = assignedAngles[row.code];
    const primaryText = buildBudgetExplanationText(row, occurrenceByKey, assignedAngle) || "Fits within range with typical day-to-day costs still worth watching.";
    const text = dedupeBudgetExplanationText(primaryText, row, seenTexts, assignedAngle);
    seenTexts.add(text);

    return {
      code: row.code,
      name: row.name,
      text,
    };
  });
};

const pushUniqueLabel = (items, value) => {
  if (!value) return;
  if (!items.some((item) => item.toLowerCase() === value.toLowerCase())) {
    items.push(value);
  }
};

const CAUTIONARY_REASON_PATTERNS = [
  /less convincing/i,
  /\bweaker\b/i,
  /not one of the strongest/i,
  /harder to justify/i,
  /\bdownside\b/i,
  /\bdrawback\b/i,
  /\btradeoffs?\b/i,
  /\bconcern\b/i,
  /require more compromise/i,
  /less supported/i,
  /less straightforward/i,
  /may feel thinner/i,
  /less margin/i,
  /may still appeal overall, but/i,
  /not the easiest fit overall/i,
];

const isCautionaryReason = (text = "") => CAUTIONARY_REASON_PATTERNS.some((pattern) => pattern.test(text));
const NON_DISTINCT_REASON_TAGS = new Set(["personalized", "comparison"]);
const REASON_GROUP_ALIASES = {
  budget: "affordability",
  value: "affordability",
  healthcare: "healthcare",
  internet: "connectivity",
  infrastructure: "connectivity",
  remote: "work-setup",
  career: "work-setup",
  urban: "work-setup",
  retiree: "financial-independence",
  community: "community",
  integration: "community",
  safety: "stability",
  stability: "stability",
  climate: "climate",
  language: "language",
  family: "household",
  culture: "lifestyle",
  nature: "lifestyle",
  lifestyle: "lifestyle",
  ease: "pathway",
  mobility: "pathway",
  comparison: "comparison",
  fallback: "fallback"
};
const REASON_SIGNATURE_STOP_WORDS = new Set([
  "a", "an", "and", "are", "as", "at", "be", "because", "can", "country", "day", "does", "for", "from", "here",
  "if", "in", "into", "is", "it", "its", "may", "more", "of", "on", "or", "overall", "still", "than", "that",
  "the", "their", "there", "they", "this", "to", "up", "well", "with", "you", "your"
]);

const Orientation = ({ user }) => {
  const navigate = useNavigate();
  const [orientation, setOrientation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState({});
  const [isProfileSummaryExpanded, setIsProfileSummaryExpanded] = useState(true);
  const [isCurrentLifeModuleExpanded, setIsCurrentLifeModuleExpanded] = useState(false);
  const [showPlanAvailabilityDialog, setShowPlanAvailabilityDialog] = useState(false);
  const hasTrackedResultsViewedRef = useRef(false);
  const planAccess = user?.plan_access || {};
  const hasPaidPlan = Boolean(user?.has_paid);
  const isLeanResultsPreview = RESULTS_PRESENTATION_MODE === "lean-preview";
  const showExpandedCountryDetails = !isLeanResultsPreview;
  const showBudgetRealityCheck = !isLeanResultsPreview;
  const showResultsMeaningSection = !isLeanResultsPreview;

  useEffect(() => {
  document.title = "Assessment – Results | Relocation Clarity";
    return () => {
      document.title = "Relocation Clarity";
    };
  }, []);

  useEffect(() => {
    fetchOrientation();
  }, []);

  useEffect(() => {
    if (!orientation || hasTrackedResultsViewedRef.current) return;

    const topCountries = orientation?.top_countries || [];
    trackGaEvent("results_viewed", {
      page_path: getGaPagePath(),
      results_tier: "free",
      results_surface: "assessment_results",
      lead_country: topCountries[0]?.name || null,
      top_country_count: topCountries.length,
    });
    hasTrackedResultsViewedRef.current = true;
  }, [orientation]);

  const fetchOrientation = async () => {
    try {
      const response = await axios.get(`${API}/orientation`, { withCredentials: true });
      setOrientation(response.data);
    } catch (error) {
      console.error("Orientation error:", error);
      if (error.response?.status === 400) {
        toast.error("Please complete the assessment first");
        navigate("/assessment");
      } else {
        toast.error("Failed to load assessment results");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    const canAccessCheckout = canUserAccessPlanCheckout(user);
    trackGaEvent("plan_unlock_clicked", {
      source: "assessment_results",
      checkout_allowed: canAccessCheckout,
    });

    if (!canAccessCheckout) {
      setShowPlanAvailabilityDialog(true);
      return;
    }

    try {
      const response = await axios.post(
        `${API}/payments/checkout`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      trackGaEvent("checkout_started", {
        source: "assessment_results",
        session_id: response.data.session_id,
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Checkout error:", error);
      if (error.response?.status === 403 && error.response?.data?.detail?.code === "plans_launch_pending") {
        setShowPlanAvailabilityDialog(true);
        return;
      }
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Assessment – Results",
          url: shareUrl
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Assessment link copied to clipboard.");
      }
    } catch (error) {
      toast.error("Unable to share results. Please try again.");
    }
  };

  const formatLabel = (value, labels) => {
    if (!value) return "Not specified";
    return labels[value] || value;
  };

  const budgetLabels = {
    low: "Lower budget",
    medium: "Moderate budget",
    high: "Higher budget",
    very_high: "Premium budget"
  };

  const timelineLabels = {
    asap: "As soon as possible",
    "6_months": "Within 6–12 months",
    "1_year": "Around 1 year",
    "2_years": "2+ years",
    exploring: "Exploring options"
  };

  const climateLabels = {
    warm: "Warm / Mediterranean",
    temperate: "Temperate / Mild",
    cold: "Cold climate",
    tropical: "Tropical",
    any: "Flexible"
  };

  const languageLabels = {
    fluent: "Comfortable in local language",
    basic: "Open to learning",
    willing_learn: "Open to learning",
    english_only: "English preferred",
    flexible: "Flexible",
    any: "Open to any language environment"
  };

  const healthcareLabels = {
    very_important: "Very important",
    important: "Important",
    somewhat_important: "Somewhat important",
    moderate: "Moderate importance",
    not_important: "Not a priority",
    not_priority: "Not a major priority"
  };

  const intendedStayLabels = {
    trial_exploratory_move: "Trial stay / exploratory move",
    "6_12_month_stay": "6–12 month stay",
    long_term_relocation: "Long-term relocation",
    split_time_between_countries: "Split time between countries"
  };

  const workAbroadLabels = {
    no: "Does not depend on earning income after the move",
    remote_self_employed: "Remote or self-employed income",
    local_employment: "Local employment",
    not_sure: "Not sure yet"
  };

  const expatCommunityLabels = {
    very_important: "Very important",
    important: "Important",
    moderate: "Moderately important",
    not_important: "Not important"
  };

  const priorityLabels = {
    cost_of_living: "Long-term affordability",
    healthcare: "Healthcare quality",
    safety: "Safety & stability",
    career: "Career opportunities",
    nature: "Nature & outdoors",
    culture: "Daily pace & lifestyle fit",
    climate: "Climate & weather",
    taxes: "Tax efficiency",
    education: "Education quality",
    community: "Expat community",
    language_transition_ease: "Language comfort & ease of transition",
    residency_pathway_practicality: "Residency pathway practicality",
    infrastructure_daily_convenience: "Infrastructure & daily convenience"
  };

  const budgetTargets = {
    low: 60,
    medium: 75,
    high: 90,
    very_high: 100
  };

  const countryRegions = {
    Portugal: "Southern Europe",
    Spain: "Southern Europe",
    France: "Western Europe",
    Greece: "Southern Europe",
    Mexico: "North America",
    "Costa Rica": "Central America",
    Panama: "Central America",
    Thailand: "Southeast Asia",
    Malaysia: "Southeast Asia"
  };

  const highInternetCodes = new Set(["PT", "ES", "FR", "DE", "NL", "CA", "NZ", "AU", "JP", "SG", "MY", "TH"]);
  const strongExpatCommunityCodes = new Set(["PT", "ES", "MX", "CR", "PA", "TH", "MY", "CA", "AU", "NZ", "SG", "GR", "HR", "UY"]);
  const remoteFriendlyCodes = new Set(["PT", "ES", "GR", "TH", "CR", "PA", "MY", "MX", "HR"]);
  const localEmploymentCodes = new Set(["DE", "CA", "NL", "AU", "SG", "JP", "ES", "PT", "FR"]);
  const passiveIncomeCodes = new Set(["PT", "ES", "CR", "PA", "MX", "GR", "UY", "IT", "HR"]);
  const longTermStrengthCodes = new Set(["PT", "ES", "FR", "DE", "CA", "NL", "NZ", "AU", "JP", "SG", "UY"]);
  const warmClimateCodes = new Set(["PT", "ES", "CR", "MX", "TH", "PA", "EC", "MY", "CO", "VN", "AU", "IT", "GR", "HR"]);
  const temperateClimateCodes = new Set(["PT", "ES", "FR", "DE", "NL", "NZ", "IT", "HR", "UY", "AR", "CL", "CA"]);
  const tropicalClimateCodes = new Set(["CR", "TH", "PA", "MY", "VN", "SG"]);
  const safetyPriorityCodes = new Set(["PT", "ES", "CA", "NZ", "AU", "JP", "SG", "DE", "NL", "UY", "HR"]);
  const familyFriendlyCodes = new Set(["PT", "ES", "CA", "NZ", "AU", "FR", "DE", "NL", "UY"]);
  const cultureForwardCodes = new Set(["IT", "FR", "ES", "MX", "JP", "AR", "CO", "PT", "GR"]);
  const natureForwardCodes = new Set(["CR", "NZ", "CA", "CL", "HR", "PT", "GR", "EC"]);
  const usRetireePresenceTierLabels = {
    High: "Established",
    Moderate: "Moderate",
    Limited: "Limited"
  };
  const usRetireePresenceDescriptions = {
    Established: "Large, well-established U.S. retiree community with visible expat hubs.",
    Moderate: "Moderate U.S. retiree presence, with some established expat areas.",
    Limited: "Smaller visible U.S. retiree community."
  };
  const resultsWatchForFallbacks = {
    PT: "Housing pressure in Lisbon, Porto, and the Algarve can change the budget picture faster than Portugal’s overall reputation suggests.",
    ES: "Regional differences in housing, healthcare access, and local rules matter more here than the headline Spain picture suggests.",
    MX: "Private healthcare can be strong, but the daily experience changes sharply by city, neighborhood, and what you are willing to pay.",
    CR: "The strongest practical setup sits in the Central Valley and established expat corridors; outside them, specialist care and infrastructure drop faster.",
    PA: "Panama can feel straightforward at first, but much of the practical case still depends on whether Panama City works for your real day-to-day life.",
    UY: "Montevideo does much more of the healthcare, housing, and service work than the country-level reputation suggests.",
    IT: "Regional variation changes healthcare access, paperwork speed, and daily convenience more than the Italy label suggests.",
    GR: "Specialist care and smoother year-round services cluster around Athens and the larger hubs, so island or smaller-town living needs more planning.",
    FR: "Healthcare quality is strong, but paperwork, sequencing, and local-language administration usually ask more of you than the lifestyle image suggests.",
    CA: "The cultural familiarity helps, but housing costs and regional healthcare access can change the practical picture faster than the stable-country image suggests.",
    AU: "The English-speaking ease is real, but housing costs and distance can make the practical commitment heavier than it first appears.",
    NZ: "The softer pace is appealing, but specialist access, scale, and distance matter more once the move becomes long-term rather than aspirational.",
    DE: "The systems are reliable, but insurance structure, paperwork, and local-language admin usually carry more day-to-day weight than newcomers expect.",
    NL: "Housing shortages can turn an otherwise efficient move into a much harder city-by-city search.",
    SG: "Housing, insurance, and routine city costs leave very little room for a casual trial-and-error move.",
    HR: "The Adriatic lifestyle is appealing, but year-round service depth and route flexibility are narrower than the coastal image suggests.",
    TH: "Private hospitals can be excellent, but the long-stay plan only works cleanly when you fit a formal visa category and insurance setup.",
    MY: "English usability and hospital quality are strong, but the practical case depends more on program fit and policy stability than the value story alone suggests.",
    JP: "The systems are excellent, but language dependence and administrative specificity become more significant once the move turns practical.",
    AR: "Argentina can feel generous on cost and culture, but inflation and currency volatility make the practical picture less steady than it first appears.",
    CL: "Chile feels structured and stable, but the process and cost picture can stay more formal and less forgiving than the softer-landing options.",
    CO: "Colombia can feel dynamic and appealing, but the practical experience changes quickly with city, neighborhood, and provider choice.",
    VN: "Vietnam can look excellent on lifestyle value, but long-run residency certainty and hospital reliance ask harder practical questions.",
    EC: "Ecuador can look attractive on cost, but the strongest healthcare and service experience stays concentrated in a smaller set of city corridors."
  };
  const usRetireePresenceByCountry = {
    PT: "High",
    ES: "High",
    MX: "High",
    CR: "High",
    PA: "High",
    CA: "High",
    GR: "Moderate",
    FR: "Moderate",
    TH: "Moderate",
    MY: "Moderate",
    EC: "Moderate",
    CO: "Moderate",
    DE: "Moderate",
    NZ: "Moderate",
    AU: "Moderate",
    IT: "Moderate",
    HR: "Moderate",
    UY: "Moderate",
    NL: "Limited",
    VN: "Limited",
    JP: "Limited",
    SG: "Limited",
    AR: "Limited",
    CL: "Limited"
  };
  const usRetireePresenceTooltip = "Based on U.S. Social Security payments sent abroad. This offers a rough signal of how visible the U.S. retiree community is, but it does not determine whether a country is the right fit for your goals.";

  const bestRegionsByCountry = {
    Portugal: [
      { name: "Lisbon", info: "Capital city with strong expat infrastructure and transit." },
      { name: "Porto", info: "Lower-cost coastal city with a growing tech scene." },
      { name: "Algarve", info: "Warm coastal region popular for a slower lifestyle." }
    ],
    Spain: [
      { name: "Valencia", info: "Mediterranean city balancing affordability and lifestyle." },
      { name: "Madrid", info: "Capital with robust healthcare access and job markets." },
      { name: "Malaga", info: "Coastal hub with strong expat communities." }
    ],
    France: [
      { name: "Lyon", info: "Mid-sized city with quality healthcare and transit." },
      { name: "Bordeaux", info: "Cultural hub with strong lifestyle amenities." },
      { name: "Montpellier", info: "Sunny southern city with lower costs." }
    ],
    Greece: [
      { name: "Athens", info: "Capital with international healthcare access." },
      { name: "Thessaloniki", info: "Coastal city with a slower pace and strong culture." },
      { name: "Chania", info: "Island lifestyle with strong expat interest." }
    ],
    Italy: [
      { name: "Bologna", info: "Well-connected city with strong healthcare access and a manageable day-to-day pace." },
      { name: "Turin", info: "Northern city with lower costs than Milan and solid infrastructure." },
      { name: "Puglia", info: "Southern region that can work well if climate and lifestyle matter more than big-city access." }
    ],
    Uruguay: [
      { name: "Montevideo", info: "Best fit for healthcare access, infrastructure, and day-to-day convenience." },
      { name: "Punta del Este", info: "Coastal option if lifestyle and seasonal pace matter more than city access." },
      { name: "Colonia del Sacramento", info: "Smaller historic city that can suit a slower, lower-friction setup." }
    ],
    Canada: [
      { name: "Victoria", info: "Milder climate and calmer pace with strong healthcare access." },
      { name: "Halifax", info: "Smaller coastal city with easier day-to-day pace than Toronto or Vancouver." },
      { name: "Calgary", info: "Practical option if infrastructure and long-term stability matter more than coastal lifestyle." }
    ],
    "New Zealand": [
      { name: "Wellington", info: "Best fit for infrastructure, walkability, and day-to-day services." },
      { name: "Christchurch", info: "Stronger value option if space and practical day-to-day living matter." },
      { name: "Tauranga", info: "Coastal city that works well if climate and outdoor access matter more." }
    ],
    Australia: [
      { name: "Adelaide", info: "Often the best value fit if affordability matters alongside healthcare and infrastructure." },
      { name: "Brisbane", info: "Warmer major-city option with easier access to outdoor living." },
      { name: "Perth", info: "Useful comparison if climate and lifestyle matter more than closeness to other hubs." }
    ],
    Japan: [
      { name: "Fukuoka", info: "Often the easiest balance of livability, infrastructure, and day-to-day pace." },
      { name: "Yokohama", info: "Works well if healthcare depth and major-metro access matter more." },
      { name: "Kobe", info: "Strong comparison if you want urban convenience with a slightly calmer feel than Tokyo." }
    ],
    Singapore: [
      { name: "East Coast", info: "Best fit if daily convenience and a calmer residential feel both matter." },
      { name: "Novena", info: "Strong option if healthcare access and centrality matter most." },
      { name: "Holland Village", info: "Useful comparison if community feel and lifestyle ease matter more than cost." }
    ],
    Germany: [
      { name: "Munich", info: "Best fit if infrastructure, safety, and healthcare are the top priorities." },
      { name: "Hamburg", info: "Strong alternative if lifestyle and day-to-day balance matter more than southern cost levels." },
      { name: "Leipzig", info: "Useful comparison if value matters more than the largest-city experience." }
    ],
    Netherlands: [
      { name: "Utrecht", info: "Often the best balance of transit, livability, and day-to-day convenience." },
      { name: "The Hague", info: "Stronger fit if international access and a calmer pace than Amsterdam matter." },
      { name: "Eindhoven", info: "Useful comparison if infrastructure and value matter more than cultural depth." }
    ],
    Croatia: [
      { name: "Zagreb", info: "Best fit if infrastructure and year-round practicality come first." },
      { name: "Split", info: "Coastal option if climate and lifestyle matter more than big-city systems." },
      { name: "Istria", info: "Strong regional choice if slower pace and scenery matter most." }
    ],
    Chile: [
      { name: "Santiago", info: "Best fit for healthcare, services, and practical day-to-day setup." },
      { name: "Viña del Mar", info: "Coastal option if lifestyle and climate matter more than pure practicality." },
      { name: "Puerto Varas", info: "Good comparison if scenery and outdoor access are carrying more weight." }
    ],
    Ecuador: [
      { name: "Cuenca", info: "Often the best all-around fit for value, climate, and long-term livability." },
      { name: "Quito", info: "Useful if healthcare access and larger-city services matter more." },
      { name: "Manta", info: "Coastal option if warmer weather and a simpler pace matter most." }
    ],
    Argentina: [
      { name: "Mendoza", info: "Best fit if climate, wine-country lifestyle, and day-to-day pace matter most." },
      { name: "Córdoba", info: "Useful balance of practicality and value outside Buenos Aires." },
      { name: "Buenos Aires", info: "Stronger option if urban culture and healthcare depth matter more than simplicity." }
    ],
    Colombia: [
      { name: "Medellín", info: "Often the strongest fit for climate, lifestyle, and expat familiarity." },
      { name: "Pereira", info: "Useful comparison if value and a smaller-city pace matter more." },
      { name: "Bogotá", info: "Best fit if infrastructure and healthcare depth outweigh climate preferences." }
    ],
    Vietnam: [
      { name: "Da Nang", info: "Best fit if beach access, day-to-day ease, and value all matter at once." },
      { name: "Ho Chi Minh City", info: "Stronger option if infrastructure and work-linked practicality matter more." },
      { name: "Hanoi", info: "Useful comparison if culture and a more established city feel matter more than climate." }
    ],
    Mexico: [
      { name: "Mexico City", info: "Major hub with extensive services and transit." },
      { name: "Merida", info: "Lower-cost city with strong safety reputation." },
      { name: "Puerto Vallarta", info: "Coastal community with expat amenities." }
    ],
    "Costa Rica": [
      { name: "Escazu", info: "Modern suburb with healthcare access." },
      { name: "Tamarindo", info: "Beach region popular with remote professionals." },
      { name: "Heredia", info: "Central Valley city with a moderate climate." }
    ],
    Panama: [
      { name: "Panama City", info: "International hub with strong infrastructure." },
      { name: "Boquete", info: "Mountain town with cooler climate." },
      { name: "Coronado", info: "Coastal area popular with retirees." }
    ],
    Thailand: [
      { name: "Chiang Mai", info: "Lower-cost city with strong expat networks." },
      { name: "Bangkok", info: "Capital with extensive healthcare and transit." },
      { name: "Phuket", info: "Island region with resort infrastructure." }
    ],
    Malaysia: [
      { name: "Kuala Lumpur", info: "Capital with modern infrastructure." },
      { name: "Penang", info: "Island city with cultural depth and lower costs." },
      { name: "Johor Bahru", info: "Southern city with proximity to Singapore." }
    ]
  };

  const fallbackRegions = [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const summary = orientation?.summary || {};
  const isRetirementOrientedUser = (() => {
    if (summary.is_retirement_oriented) return true;

    const occupation = String(summary.occupation || "").toLowerCase();
    const moveSupportPaths = new Set(summary.move_support_paths || []);
    const motivations = new Set(summary.motivations || []);

    return (
      summary.family_status === "retired"
      || /retir|pension|empty nester|pre-retir|near-retir|financially independent|passive income/.test(occupation)
      || moveSupportPaths.has("passive_income")
      || moveSupportPaths.has("retirement_income")
      || motivations.has("retirement_planning")
    );
  })();
  const getUsRetireePresence = (country) => {
    if (country?.us_retiree_presence?.tier && country?.us_retiree_presence?.description) {
      const normalizedTier = usRetireePresenceTierLabels[country.us_retiree_presence.tier] || country.us_retiree_presence.tier;
      return {
        tier: normalizedTier,
        description: country.us_retiree_presence.description || usRetireePresenceDescriptions[normalizedTier] || usRetireePresenceDescriptions.Limited
      };
    }

    const rawTier = usRetireePresenceByCountry[country?.code] || "Limited";
    const tier = usRetireePresenceTierLabels[rawTier] || rawTier;
    return {
      tier,
      description: usRetireePresenceDescriptions[tier]
    };
  };
  const decisionContext = orientation?.decision_context || {};
  const familyContextLabels = {
    single: "a solo move",
    couple: "planning as a couple",
    family_young: "planning around younger children",
    family_older: "planning around older children"
  };

  const coreParameters = [
    {
      label: "Budget",
      value: formatLabel(summary.budget, budgetLabels)
    },
    {
      label: "Budget for Living Abroad",
      value: formatLabel(summary.budget_abroad, budgetLabels)
    },
    {
      label: "Timeline",
      value: formatLabel(summary.timeline, timelineLabels)
    },
    {
      label: "Intended stay",
      value: formatLabel(summary.intended_length_of_stay, intendedStayLabels)
    },
    {
      label: "Work abroad",
      value: formatLabel(summary.work_abroad_intent, workAbroadLabels)
    },
    {
      label: "Infrastructure",
      value: formatLabel(summary.infrastructure_expectation, {
        world_class: "World-class",
        modern: "Modern",
        adequate: "Adequate",
        basic: "Basic"
      })
    }
  ];

  const topPriorities = summary.priorities || [];
  const healthcareIsPrimaryDriver = topPriorities.slice(0, 3).includes("healthcare") || summary.healthcare_importance === "very_important";
  const topMotivationLabels = normalizeMotivations(summary.motivations || [])
    .map((motivation) => getMotivationLabel(motivation))
    .filter(Boolean)
    .slice(0, 3);
  const topLifestyleRefinerLabels = (summary.lifestyle_preferences || [])
    .map((preference) => LIFE_REFINER_LABELS[preference] || preference.replace(/_/g, " "))
    .filter(Boolean)
    .slice(0, 3);

  const keyPreferences = [
    {
      label: "Climate",
      value: formatLabel(summary.climate, climateLabels)
    },
    {
      label: "Language Flexibility",
      value: formatLabel(summary.language_flexibility, languageLabels)
    },
    {
      label: "Healthcare importance",
      value: formatLabel(summary.healthcare_importance, healthcareLabels)
    },
    {
      label: "Reliable internet",
      value: summary.need_reliable_internet ? "Yes" : "No"
    },
    {
      label: "Expat community",
      value: formatLabel(summary.expat_community_importance, expatCommunityLabels)
    }
  ];
  const topCountries = orientation?.top_countries || [];
  const selectedCountryFits = orientation?.selected_country_fits || [];
  const decisionLeadCountry = decisionContext.lead_country || topCountries[0]?.name || "";
  const decisionAlternativeCountry = decisionContext.closest_alternative || topCountries[1]?.name || "";
  const decisionSignal = decisionContext.decision_signal?.trim() || "";
  const decisionTradeoff = decisionContext.main_tradeoff?.trim() || "";
  const leadCountryData = topCountries.find((country) => country.name === decisionLeadCountry) || topCountries[0] || null;
  const alternativeCountryData = topCountries.find((country) => country.name === decisionAlternativeCountry) || topCountries[1] || null;
  const formattedTopPriorities = topPriorities.map((priority) => priorityLabels[priority] || priority.replace(/_/g, " "));
  const priorityRankWeights = [1.65, 1.28, 1, 0.82, 0.68];
  const rankedPriorityIndexMap = new Map(topPriorities.map((priority, index) => [priority, index]));
  const getPriorityRankWeight = (priority) => {
    const priorityIndex = rankedPriorityIndexMap.get(priority);
    if (priorityIndex === undefined) return null;
    return priorityRankWeights[priorityIndex] || priorityRankWeights[priorityRankWeights.length - 1];
  };
  const getInterpretationPriorityLabel = (priority) => {
    if (priority === "cost_of_living") return "long-term affordability";
    if (priority === "healthcare") return "healthcare confidence";
    if (priority === "safety") return "safety and day-to-day stability";
    if (priority === "career") return "career practicality";
    if (priority === "nature") return "outdoor access and natural setting";
    if (priority === "culture") return "day-to-day pace";
    if (priority === "climate") {
      return summary.climate && summary.climate !== "any"
        ? `${formatLabel(summary.climate, climateLabels).toLowerCase()} living`
        : "climate fit";
    }
    if (priority === "taxes") return "tax efficiency";
    if (priority === "education") return "education quality";
    if (priority === "community") return "social landing ease";
    if (priority === "language_transition_ease") {
      return summary.language_flexibility === "english_only"
        ? "language comfort and easier English navigation"
        : "language comfort and transition ease";
    }
    if (priority === "residency_pathway_practicality") return "residency practicality";
    if (priority === "infrastructure_daily_convenience") return "reliable daily convenience";
    return priorityLabels[priority] || priority.replace(/_/g, " ");
  };
  const compactTopPriorities = formattedTopPriorities.length
    ? `${formattedTopPriorities
      .slice(0, 2)
      .join(", ")}${topPriorities.length > 2 ? ` +${topPriorities.length - 2} more` : ""}`
    : "Not specified";
  const naturalLanguageIntro = summary.natural_language_intro?.trim() || "";
  const summarizedNaturalLanguageIntro = summarizeNaturalLanguageIntro(naturalLanguageIntro);
  const personalizationThemes = derivePersonalizationThemes(naturalLanguageIntro);
  const explanationFocusLabels = [];
  personalizationThemes.forEach((theme) => pushUniqueLabel(explanationFocusLabels, theme.label));

  if (summary.strict_budget_mode || ["low", "medium"].includes(summary.budget_abroad || summary.budget)) {
    pushUniqueLabel(explanationFocusLabels, "keeping living costs aligned to your budget");
  } else if (summary.budget_abroad || summary.budget) {
    pushUniqueLabel(explanationFocusLabels, "keeping cost and quality in balance");
  }

  if (healthcareIsPrimaryDriver) {
    pushUniqueLabel(explanationFocusLabels, "healthcare confidence");
  }

  if (summary.need_reliable_internet) {
    pushUniqueLabel(explanationFocusLabels, "reliable day-to-day connectivity");
  }

  if (["very_important", "important"].includes(summary.expat_community_importance)) {
    pushUniqueLabel(explanationFocusLabels, "a smoother social landing");
  }

  if (summary.timeline === "asap") {
    pushUniqueLabel(explanationFocusLabels, "a lower-friction relocation path");
  } else if (summary.intended_length_of_stay === "long_term_relocation") {
    pushUniqueLabel(explanationFocusLabels, "long-term relocation durability");
  }

  if (summary.work_abroad_intent === "remote_self_employed") {
    pushUniqueLabel(explanationFocusLabels, summary.need_reliable_internet ? "remote-work practicality and internet reliability" : "remote-work practicality");
  } else if (summary.work_abroad_intent === "local_employment") {
    pushUniqueLabel(explanationFocusLabels, "work-dependent relocation feasibility");
  } else if (summary.work_abroad_intent === "no") {
    pushUniqueLabel(explanationFocusLabels, "living well without depending on local employment");
  }

  if (topPriorities.includes("safety")) {
    pushUniqueLabel(explanationFocusLabels, "safety and day-to-day stability");
  }

  if (topPriorities.includes("climate") && summary.climate && summary.climate !== "any") {
    pushUniqueLabel(explanationFocusLabels, `${formatLabel(summary.climate, climateLabels).toLowerCase()} living`);
  }

  if (topPriorities.includes("language_transition_ease")) {
    pushUniqueLabel(explanationFocusLabels, "language comfort and a smoother transition");
  }

  if (topPriorities.includes("nature")) {
    pushUniqueLabel(explanationFocusLabels, "outdoor access and a stronger natural setting");
  }

  normalizeMotivations(summary.motivations || []).forEach((motivation) => {
    const motivationLabels = {
      safety: "feeling safer",
      political_climate: "a more stable political environment",
      lower_cost_of_living: "lower living costs",
      better_healthcare: "better healthcare access",
      slower_pace_less_stress: "a slower pace of life",
      work_or_business_opportunities: "work or business opportunities",
      retirement_planning: "retirement planning",
      education_or_family_reasons: "education or family needs",
      adventure_or_new_experience: "experiencing something new",
      closer_to_family: "being closer to family"
    };
    if (motivationLabels[motivation]) {
      pushUniqueLabel(explanationFocusLabels, motivationLabels[motivation]);
      return;
    }
    pushUniqueLabel(explanationFocusLabels, getMotivationLabel(motivation));
  });

  (summary.lifestyle_preferences || []).forEach((preference) => {
    const lifestyleLabels = {
      arts_culture: "arts and culture",
      fine_dining: "food and dining quality",
      golf_access: "golf access",
      legalized_cannabis: "cannabis policy fit",
      nightlife_entertainment: "nightlife and entertainment",
      shopping: "shopping convenience",
      sightseeing: "sightseeing access",
      public_transportation: "strong public transportation",
      walkability: "walkable daily life",
      beach_environment: "coastal living",
      outdoor_activities: "outdoor access"
    };
    if (lifestyleLabels[preference]) pushUniqueLabel(explanationFocusLabels, lifestyleLabels[preference]);
  });

  if (["family_young", "family_older"].includes(summary.family_status)) {
    pushUniqueLabel(explanationFocusLabels, "household stability");
  }

  const explanationFocusSummary = formatPriorityList(explanationFocusLabels.slice(0, 3));
  const profileSetupSummary = formatPriorityList(
    [
      familyContextLabels[summary.family_status],
      summary.timeline === "asap"
        ? "with relatively near-term timing"
        : summary.timeline === "exploring"
          ? "while still in exploration mode"
          : "",
      summary.work_abroad_intent === "remote_self_employed"
        ? "remote-work flexibility in mind"
        : summary.work_abroad_intent === "local_employment"
          ? "local work practicality in mind"
          : summary.work_abroad_intent === "no"
            ? "a move that does not depend on local employment"
            : ""
    ].filter(Boolean).slice(0, 3)
  );
  const personalizedProfileHelperText = explanationFocusSummary
    ? profileSetupSummary
      ? `These results are being framed ${/^(with|without|while)\b/i.test(profileSetupSummary)
        ? profileSetupSummary
        : /\bin mind\b/i.test(profileSetupSummary)
          ? `with ${profileSetupSummary}`
          : `for ${profileSetupSummary}`}, with added attention to ${explanationFocusSummary}.`
      : `These results are being framed with added attention to ${explanationFocusSummary}.`
    : "A quick view of the inputs shaping these results.";
  const driverScores = new Map();
  const addDriverScore = (label, score) => {
    if (!label) return;
    driverScores.set(label, Math.max(driverScores.get(label) || 0, score));
  };
  const addPriorityDriverScore = (priority, primaryBaseScore, supportingBaseScore = 0) => {
    const label = getInterpretationPriorityLabel(priority);
    const rankWeight = getPriorityRankWeight(priority);
    const score = rankWeight ? Math.round(primaryBaseScore * rankWeight) : supportingBaseScore;
    if (score > 0) addDriverScore(label, score);
  };

  addPriorityDriverScore("cost_of_living", 10, summary.strict_budget_mode || ["low", "medium"].includes(summary.budget_abroad || summary.budget) ? 7 : 0);
  addPriorityDriverScore("healthcare", 10, healthcareIsPrimaryDriver ? 7 : 0);
  addPriorityDriverScore("residency_pathway_practicality", 10, summary.work_abroad_intent || summary.timeline === "asap" ? 6 : 0);
  addPriorityDriverScore("language_transition_ease", 9, summary.language_flexibility === "english_only" ? 6 : 0);
  addPriorityDriverScore("nature", 9);
  addPriorityDriverScore("infrastructure_daily_convenience", 8, summary.need_reliable_internet ? 6 : 0);
  addPriorityDriverScore("safety", 8);
  addPriorityDriverScore("culture", 8);
  addPriorityDriverScore("climate", 8, summary.climate && summary.climate !== "any" ? 5 : 0);

  const focusedDriverLabels = Array.from(driverScores.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([label]) => label);
  const rankedInterpretationLabels = topPriorities
    .slice(0, 3)
    .map((priority) => getInterpretationPriorityLabel(priority))
    .filter(Boolean);
  const leadInterpretationLabel = rankedInterpretationLabels[0] || null;
  const supportingPriorityLabels = rankedInterpretationLabels.slice(1, 3);
  const secondaryInterpretationLabels = focusedDriverLabels.filter((label) => !rankedInterpretationLabels.includes(label)).slice(0, 2);
  const suppressedExplanationMetricKeys = new Set();
  if (!healthcareIsPrimaryDriver) {
    suppressedExplanationMetricKeys.add("healthcare_quality");
  }
  const visiblePracticalFactorLabels = [
    "affordability",
    "feasibility",
    !suppressedExplanationMetricKeys.has("healthcare_quality") ? "healthcare" : null,
  ].filter(Boolean);
  const resultsInterpretationCopy = buildResultsMeaningInterpretationCopy({
    leadName: decisionLeadCountry || "The lead option",
    alternativeName: decisionAlternativeCountry,
    leadInterpretationLabel,
    supportingPriorityLabels,
    secondaryInterpretationLabels,
  });
  const focusedDriverText = focusedDriverLabels.join(" ").toLowerCase();
  const omittedHighSalienceLabels = [];

  if (
    topPriorities.includes("education") &&
    !focusedDriverText.includes("education") &&
    ["family_young", "family_older"].includes(summary.family_status) &&
    leadCountryData &&
    familyFriendlyCodes.has(leadCountryData.code)
  ) {
    omittedHighSalienceLabels.push("education quality");
  }

  if (
    topPriorities.includes("safety") &&
    !focusedDriverText.includes("safety") &&
    leadCountryData &&
    safetyPriorityCodes.has(leadCountryData.code)
  ) {
    omittedHighSalienceLabels.push("safety and stability");
  }

  if (
    topPriorities.includes("cost_of_living") &&
    !focusedDriverText.includes("affordability") &&
    !focusedDriverText.includes("cost") &&
    leadCountryData &&
    (summary.strict_budget_mode || (alternativeCountryData && Number(leadCountryData.cost_index || 999) <= Number(alternativeCountryData.cost_index || 999)))
  ) {
    omittedHighSalienceLabels.push("long-term affordability");
  }

  const resultsSupportPriorityCopy = omittedHighSalienceLabels.length
    ? buildResultsMeaningSupportCopy({
      omittedHighSalienceLabels,
      familyStatus: summary.family_status,
    })
    : null;
  const reasonPriorityWeights = (() => {
    const weights = new Map();
    const setWeight = (key, score) => {
      weights.set(key, Math.max(weights.get(key) || 0, score));
    };

    personalizationThemes.forEach((theme, index) => {
      const score = 9 - index;
      if (theme.key === "calm") setWeight("lifestyle", score);
      if (theme.key === "healthcare") setWeight("healthcare", score + 1);
      if (theme.key === "budget") {
        setWeight("budget", score + 1);
        setWeight("value", score);
      }
      if (theme.key === "residency") setWeight("ease", score + 1);
      if (theme.key === "city") {
        setWeight("urban", score + 1);
        setWeight("career", score);
      }
      if (theme.key === "family") {
        setWeight("family", score + 1);
        setWeight("stability", score);
      }
      if (theme.key === "remote") {
        setWeight("remote", score + 1);
        setWeight("internet", score);
      }
    });

    if (summary.strict_budget_mode || ["low", "medium"].includes(summary.budget_abroad || summary.budget)) {
      setWeight("budget", 10);
      setWeight("value", 9);
    } else if (summary.budget_abroad || summary.budget) {
      setWeight("budget", 7);
      setWeight("value", 6);
    }

    if (healthcareIsPrimaryDriver) {
      setWeight("healthcare", 10);
      setWeight("stability", 6);
    }

    if (summary.need_reliable_internet) {
      setWeight("internet", 9);
      setWeight("infrastructure", 8);
      if (summary.work_abroad_intent === "remote_self_employed") setWeight("remote", 10);
    }

    if (["very_important", "important"].includes(summary.expat_community_importance)) {
      setWeight("community", 8);
      setWeight("integration", 7);
    }

    if (summary.timeline === "asap") {
      setWeight("ease", 9);
      setWeight("mobility", 7);
    }

    if (summary.intended_length_of_stay === "long_term_relocation") {
      setWeight("stability", 8);
      setWeight("healthcare", Math.max(weights.get("healthcare") || 0, 7));
    }

    if (summary.intended_length_of_stay === "trial_exploratory_move") {
      setWeight("ease", 8);
      setWeight("lifestyle", 6);
    }

    if (summary.work_abroad_intent === "local_employment") {
      setWeight("career", 9);
      setWeight("urban", 7);
    }

    if (summary.work_abroad_intent === "no") {
      setWeight("retiree", 8);
      setWeight("ease", Math.max(weights.get("ease") || 0, 7));
    }

    if (summary.language_flexibility === "english_only") {
      setWeight("language", 8);
      setWeight("integration", Math.max(weights.get("integration") || 0, 6));
    }

    if (["family_young", "family_older"].includes(summary.family_status)) {
      setWeight("family", 9);
      setWeight("stability", Math.max(weights.get("stability") || 0, 8));
    }

    if (topPriorities.includes("safety")) {
      const safetyWeight = Math.round(9 * (getPriorityRankWeight("safety") || 1));
      setWeight("safety", safetyWeight);
      setWeight("stability", Math.max(weights.get("stability") || 0, Math.max(7, safetyWeight - 1)));
    }

    if (topPriorities.includes("healthcare")) {
      const healthcareWeight = Math.round(9 * (getPriorityRankWeight("healthcare") || 1));
      setWeight("healthcare", Math.max(weights.get("healthcare") || 0, healthcareWeight));
    }
    if (topPriorities.includes("cost_of_living")) {
      const affordabilityWeight = Math.round(9 * (getPriorityRankWeight("cost_of_living") || 1));
      setWeight("budget", Math.max(weights.get("budget") || 0, affordabilityWeight));
      setWeight("value", Math.max(weights.get("value") || 0, Math.max(7, affordabilityWeight - 1)));
    }
    if (topPriorities.includes("career")) {
      const careerWeight = Math.round(8 * (getPriorityRankWeight("career") || 1));
      setWeight("career", Math.max(weights.get("career") || 0, careerWeight));
      setWeight("urban", Math.max(weights.get("urban") || 0, Math.max(6, careerWeight - 1)));
    }
    if (topPriorities.includes("culture")) {
      setWeight("culture", Math.round(7 * (getPriorityRankWeight("culture") || 1)));
    }
    if (topPriorities.includes("nature")) {
      const natureWeight = Math.round(10 * (getPriorityRankWeight("nature") || 1));
      setWeight("nature", natureWeight);
      setWeight("lifestyle", Math.max(weights.get("lifestyle") || 0, Math.max(7, natureWeight - 3)));
    }
    if (topPriorities.includes("climate")) {
      setWeight("climate", Math.round(7 * (getPriorityRankWeight("climate") || 1)));
    }
    if (topPriorities.includes("community")) {
      const communityWeight = Math.round(8 * (getPriorityRankWeight("community") || 1));
      setWeight("community", Math.max(weights.get("community") || 0, communityWeight));
      setWeight("integration", Math.max(weights.get("integration") || 0, Math.max(6, communityWeight - 1)));
    }
    if (topPriorities.includes("language_transition_ease")) {
      const languageWeight = Math.round(10 * (getPriorityRankWeight("language_transition_ease") || 1));
      setWeight("language", languageWeight);
      setWeight("integration", Math.max(weights.get("integration") || 0, Math.max(7, languageWeight - 2)));
      setWeight("community", Math.max(weights.get("community") || 0, Math.max(6, languageWeight - 4)));
    }

    return weights;
  })();
  const compactProfileSnapshot = [
    {
      label: "Top priorities",
      value: compactTopPriorities,
      testId: "profile-summary-snapshot-priorities"
    },
    {
      label: "Budget",
      value: formatLabel(summary.budget_abroad || summary.budget, budgetLabels),
      testId: "profile-summary-snapshot-budget"
    },
    {
      label: "Climate",
      value: formatLabel(summary.climate, climateLabels),
      testId: "profile-summary-snapshot-climate"
    },
    {
      label: "Healthcare",
      value: formatLabel(summary.healthcare_importance, healthcareLabels),
      testId: "profile-summary-snapshot-healthcare"
    },
    {
      label: "Why now",
      value: topMotivationLabels.length ? `${topMotivationLabels[0]}${topMotivationLabels.length > 1 ? ` +${topMotivationLabels.length - 1} more` : ""}` : "Not specified",
      testId: "profile-summary-snapshot-why-now"
    }
  ];
  const budgetTarget = budgetTargets[summary.budget_abroad || summary.budget] || 75;
  const orderedTopCountries = (() => {
    if (!topCountries.length) return [];
    const lead = topCountries.find((country) => country.name === decisionLeadCountry) || topCountries[0];
    const alternative = topCountries.find((country) => country.name === decisionAlternativeCountry && (country.code || country.name) !== (lead.code || lead.name));
    const remaining = topCountries.filter((country) => {
      const countryKey = country.code || country.name;
      return countryKey !== (lead.code || lead.name) && countryKey !== (alternative?.code || alternative?.name);
    });

    return [lead, alternative, ...remaining].filter(Boolean);
  })();
  const topRankedCountryCards = orderedTopCountries
    .slice(0, 4)
    .map((country) => ({ country, cardType: "top" }));
  const paceSignalState = getPaceSignalState(summary);
  const resultsPaceDecisionContrast = getPaceDecisionContrast(topRankedCountryCards.map(({ country }) => country).slice(0, 2), summary);
  const resultsPaceLightMention = getPaceLightMention(summary);
  const resultsMeaningSupportLine = resultsSupportPriorityCopy || buildResultsMeaningSupportCopy({
    omittedHighSalienceLabels: [],
    familyStatus: summary.family_status,
    paceDecisionContrast: resultsPaceDecisionContrast,
    paceLightMention: resultsPaceLightMention,
  });
  const polishedDecisionSignal = buildResultsMeaningLeadLine({
    leadName: decisionLeadCountry,
    alternativeName: decisionAlternativeCountry,
    leadInterpretationLabel,
    visiblePracticalFactorLabels,
    fallbackText: decisionSignal || "Treat these as your strongest current shortlist, not a generic ranking.",
  });
  const resultsMeaningLines = [
    polishedDecisionSignal,
    resultsInterpretationCopy,
    resultsMeaningSupportLine,
  ].filter(Boolean).slice(0, 3);
  const recommendationTraceabilityLabels = dedupeOrderedLabels([
    ...rankedInterpretationLabels,
    ...focusedDriverLabels,
  ]).slice(0, 3);
  const payoffVisibilityChips = dedupeOrderedLabels([
    ...topMotivationLabels,
    ...topLifestyleRefinerLabels,
  ]).slice(0, 6);
  const hasSelectedLifestyleRefiners = topLifestyleRefinerLabels.length > 0;
  const visiblePayoffCopy = (() => {
    if (topMotivationLabels.length && topLifestyleRefinerLabels.length) {
      return `Your main priorities are doing most of the ranking work. Your answers around ${formatPriorityList(topMotivationLabels)} help explain the kind of move you are trying to make, while refiners like ${formatPriorityList(topLifestyleRefinerLabels)} help separate close calls and shape the day-to-day details we surface.`;
    }
    if (topMotivationLabels.length) {
      return `Your main priorities are doing most of the ranking work. Your answers around ${formatPriorityList(topMotivationLabels)} help explain the kind of move you are trying to make and which tradeoffs receive more emphasis.`;
    }
    if (topLifestyleRefinerLabels.length) {
      return `Your main priorities are doing most of the ranking work. Your lifestyle refiners — ${formatPriorityList(topLifestyleRefinerLabels)} — help separate close options and add more day-to-day context to the shortlist.`;
    }
    return null;
  })();
  const topRankedCountryKeys = new Set(topRankedCountryCards.map(({ country }) => country.code || country.name));
  const selectedCountryCards = selectedCountryFits
    .filter((country) => {
      const key = country.code || country.name;
      return key && !topRankedCountryKeys.has(key);
    })
    .map((country) => ({ country, cardType: "selected" }));
  const visibleCountryCards = [...topRankedCountryCards, ...selectedCountryCards];
  const displayScoreMap = buildDisplayScoreMap(visibleCountryCards);
  const leadCardCountry = orderedTopCountries[0] || null;
  const relocationBudgetValue = summary.budget_display_range || summary.budget_abroad || summary.budget;
  const relocationBudgetDetails = getRelocationBudgetDetails(relocationBudgetValue);
  const budgetRealityCheckRows = buildBudgetFitRows(topRankedCountryCards.map(({ country }) => country), relocationBudgetValue, summary.family_status);
  const budgetRealityCheckInterpretationBullets = buildBudgetFitInterpretationBullets(budgetRealityCheckRows);
  const normalizeReasonPattern = (text) => text
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9\s]/g, "")
    .split(/\s+/)
    .slice(0, 6)
    .join(" ");

  const budgetAnchor = summary.strict_budget_mode
    ? "keeping costs closely aligned to your budget"
    : ["low", "medium"].includes(summary.budget_abroad || summary.budget)
      ? "keeping costs disciplined"
      : "keeping cost and quality in balance";
  const healthcareAnchor = summary.healthcare_importance === "very_important"
    ? "medical confidence sits near the top of your decision"
    : "healthcare still carries real weight in your decision";
  const internetAnchor = "dependable internet is part of how you plan to live or work day to day";
  const communityAnchor = "having some newcomer support around you matters in the early transition";
  const safetyAnchor = "day-to-day stability matters more than novelty in this decision";
  const localWorkAnchor = "the move may need to stand up even if local employment becomes part of the plan";
  const remoteWorkAnchor = summary.need_reliable_internet
    ? "remote-work practicality depends on both setup ease and reliable connectivity"
    : "remote-work practicality matters in how you frame the move";
  const noWorkAnchor = "you want a move that can make sense without relying on local employment";
  const familyAnchor = ["family_young", "family_older"].includes(summary.family_status)
    ? "the move needs to feel steady enough for a household, not just a solo adventure"
    : "the move needs to feel livable beyond a short-term experiment";
  const climateAnchor = summary.climate === "warm"
    ? "you want everyday life to feel warm rather than cool or gray"
    : summary.climate === "temperate"
      ? "you seem to prefer a milder climate over tropical heat or colder extremes"
      : summary.climate === "tropical"
        ? "you are looking for a genuinely tropical day-to-day environment"
        : "daily environment still matters in how you picture the move";
  const timelineAnchor = summary.timeline === "asap"
    ? "you want the move to get off the ground without an unnecessarily long runway"
    : summary.intended_length_of_stay === "trial_exploratory_move"
      ? "you want room to test fit before making a deep commitment"
      : summary.intended_length_of_stay === "long_term_relocation"
        ? "you are optimizing for a move that needs to hold up well over time"
        : "you want the move to stay practical over time";

  const buildProfileAwareReasonCandidates = (country, reasonType = "fit") => {
    const candidates = [];
    const affordabilityPriority = topPriorities.includes("cost_of_living") || Boolean(summary.budget);
  const healthcarePriority = healthcareIsPrimaryDriver;
    const expatCommunityPriority = ["very_important", "important"].includes(summary.expat_community_importance);
    const climateMatchSet = summary.climate === "warm"
      ? warmClimateCodes
      : summary.climate === "temperate"
        ? temperateClimateCodes
        : summary.climate === "tropical"
          ? tropicalClimateCodes
          : null;
    const climateMismatch = climateMatchSet ? !climateMatchSet.has(country.code) : false;
    const budgetSeed = `${country.code}-budget-${summary.budget_abroad || summary.budget}-${reasonType}`;
    const healthcareSeed = `${country.code}-healthcare-${summary.healthcare_importance}-${reasonType}`;
    const climateSeed = `${country.code}-climate-${summary.climate}-${reasonType}`;
    const workSeed = `${country.code}-work-${summary.work_abroad_intent}-${summary.need_reliable_internet ? 'internet' : 'no-internet'}-${reasonType}`;
    const communitySeed = `${country.code}-community-${summary.expat_community_importance}-${reasonType}`;
    const stabilitySeed = `${country.code}-stability-${summary.family_status}-${reasonType}`;
    const timelineSeed = `${country.code}-timeline-${summary.timeline}-${summary.intended_length_of_stay}-${reasonType}`;

    if (reasonType === "fit") {
      if (affordabilityPriority && country.cost_index <= budgetTarget + 2) {
        candidates.push({
          text: pickStableOption([
            `${country.name} is easier to support when ${budgetAnchor}, because it preserves more day-to-day margin than many of the higher-cost options in your set.`,
            `If ${budgetAnchor} is part of the brief, ${country.name} makes a stronger case because the day-to-day spend is easier to keep under control.`,
            `${country.name} keeps more financial breathing room intact, which matters when ${budgetAnchor} rather than treating affordability as an afterthought.`
          ], budgetSeed),
          tags: ["budget", "value"],
          score: 26,
        });
      }

      if (healthcarePriority && country.healthcare_rating >= 4.3) {
        candidates.push({
          text: pickStableOption([
            `${country.name} becomes easier to justify when ${healthcareAnchor}, because it offers more healthcare depth than many of the lifestyle-led alternatives nearby.`,
            `Because ${healthcareAnchor}, ${country.name} gets more explanatory weight here than countries that ask you to compromise more on system confidence.`,
            `${country.name} stands up better once healthcare enters the decision seriously, which matters if ${healthcareAnchor}.`
          ], healthcareSeed),
          tags: ["healthcare", "stability"],
          score: 25,
        });
      }

      if (summary.need_reliable_internet && highInternetCodes.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} looks stronger once you account for the fact that ${internetAnchor}, because the connectivity baseline is more dependable here than in many peer options.`,
            `If ${internetAnchor}, ${country.name} is easier to defend because the infrastructure is less likely to become a daily friction point.`,
            `${country.name} carries more practical weight for your profile because ${internetAnchor}, and the infrastructure case here is stronger than in many lower-cost alternatives.`
          ], `${workSeed}-internet-fit`),
          tags: ["internet", "infrastructure", "remote"],
          score: 24,
        });
      }

      if (expatCommunityPriority && strongExpatCommunityCodes.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} gives you a better social landing if ${communityAnchor}, because it has a more established expat and newcomer layer than many of the thinner-community alternatives.`,
            `Because ${communityAnchor}, ${country.name} looks more workable early on than countries where you would need to build support from scratch.`,
            `${country.name} reads as a smoother transition if ${communityAnchor}, especially compared with options where the newcomer ecosystem feels more limited.`
          ], communitySeed),
          tags: ["community", "integration"],
          score: 22,
        });
      }

      if (topPriorities.includes("safety") && safetyPriorityCodes.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} rises for your profile because ${safetyAnchor}, and day-to-day conditions are steadier here than in the more variable options in your shortlist.`,
            `If ${safetyAnchor}, ${country.name} makes more sense because it offers a calmer baseline for daily life than several of the alternatives around it.`,
            `${country.name} holds up better once safety and predictability matter, which fits a profile where ${safetyAnchor}.`
          ], stabilitySeed),
          tags: ["safety", "stability"],
          score: 23,
        });
      }

      if (climateMatchSet?.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} fits more naturally once climate enters the picture, because ${climateAnchor}.`,
            `Climate does some real work in this recommendation: ${country.name} is easier to support if ${climateAnchor}.`,
            `${country.name} gains relevance for your profile because ${climateAnchor}, and this environment is closer to that brief than many of the mismatched alternatives.`
          ], climateSeed),
          tags: ["climate", "lifestyle"],
          score: 21,
        });
      }

      if (topPriorities.includes("culture") && cultureForwardCodes.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} feels more credible if daily culture matters in the move, because it offers a more distinctive lived experience than purely practical or interchangeable alternatives.`,
            `When cultural depth is part of what you are optimizing for, ${country.name} reads as more than a systems play — it offers a stronger sense of place.`,
            `${country.name} gets a little more weight here if you care about the texture of everyday life, not just the practical checklist.`
          ], `${country.code}-culture-${reasonType}`),
          tags: ["culture", "lifestyle"],
          score: 18,
        });
      }

      if (topPriorities.includes("nature") && natureForwardCodes.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} becomes more compelling if access to outdoors is part of the lifestyle you are actually trying to build.`,
            `For a profile that values nature in the day-to-day experience, ${country.name} carries more weight than the denser, more urban alternatives.`,
            `${country.name} fits more naturally if scenery, outdoor access, and a less boxed-in daily rhythm matter to you.`
          ], `${country.code}-nature-${reasonType}`),
          tags: ["nature", "lifestyle"],
          score: 18,
        });
      }

      if (["family_young", "family_older"].includes(summary.family_status) && familyFriendlyCodes.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} reads as a steadier household choice, which matters if ${familyAnchor}.`,
            `If ${familyAnchor}, ${country.name} makes more sense than countries that feel better suited to a looser solo move.`,
            `${country.name} holds up better for your situation because ${familyAnchor}, not just an exciting short-term relocation story.`
          ], `${country.code}-family-${reasonType}`),
          tags: ["family", "stability"],
          score: 20,
        });
      }

      if (summary.timeline === "asap" && country.visa_difficulty === "Easy") {
        candidates.push({
          text: pickStableOption([
            `${country.name} gets stronger if timing matters, because ${timelineAnchor} and the transition path here is more straightforward than in the more admin-heavy options.`,
            `Because ${timelineAnchor}, ${country.name} is easier to support than countries that ask for a slower or more bureaucratic setup.`,
            `${country.name} lines up better with your timing because ${timelineAnchor}, and the move here is easier to get moving.`
          ], timelineSeed),
          tags: ["ease", "timeline"],
          score: 22,
        });
      }

      if (summary.work_abroad_intent === "remote_self_employed" && remoteFriendlyCodes.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} becomes easier to defend because ${remoteWorkAnchor}, and the remote setup is easier to maintain here than in the less remote-friendly options.`,
            `If ${remoteWorkAnchor}, ${country.name} asks for less setup work than countries that make you solve a harder remote-work problem before the move feels stable.`,
            `${country.name} fits a remote-oriented profile more naturally, especially when ${remoteWorkAnchor}.`
          ], `${workSeed}-remote-fit`),
          tags: ["remote", "ease", "internet"],
          score: 23,
        });
      }

      if (summary.work_abroad_intent === "local_employment" && localEmploymentCodes.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} gets easier to justify if ${localWorkAnchor}, because it offers a stronger mix of job-market depth, systems, and city infrastructure than many lifestyle-first alternatives.`,
            `Because ${localWorkAnchor}, ${country.name} reads as a more workable base than countries where the case is stronger for lifestyle than for employment-dependent relocation.`,
            `${country.name} carries more practical weight for your profile if ${localWorkAnchor}.`
          ], `${workSeed}-local-fit`),
          tags: ["career", "urban", "stability"],
          score: 22,
        });
      }

      if (summary.work_abroad_intent === "no" && passiveIncomeCodes.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} makes more sense if ${noWorkAnchor}, because the move does not rely as heavily on building a local work case first.`,
            `If ${noWorkAnchor}, ${country.name} feels more coherent than options that are easier to justify only with a stronger employment story behind them.`,
            `${country.name} is easier to support for a self-funded move, especially if ${noWorkAnchor}.`
          ], `${workSeed}-no-work-fit`),
          tags: ["ease", "retiree", "value"],
          score: 20,
        });
      }
    } else {
      if (affordabilityPriority && country.cost_index >= budgetTarget + 8) {
        candidates.push({
          text: pickStableOption([
            `${country.name} becomes harder to justify if ${budgetAnchor}, because housing and everyday spend leave you less margin than the better-aligned options in your results.`,
            `If ${budgetAnchor}, ${country.name} asks for more flexibility than the countries that fit your cost profile more cleanly.`,
            `${country.name} may still appeal, but it stretches the budget case more than the alternatives that stay closer to your cost brief.`
          ], budgetSeed),
          tags: ["budget", "value"],
          score: 26,
        });
      }

      if (healthcarePriority && country.healthcare_rating < 4.1) {
        candidates.push({
          text: pickStableOption([
            `${country.name} asks for more compromise if ${healthcareAnchor}, because it does not offer the same level of healthcare confidence as the stronger system leaders in your set.`,
            `Because ${healthcareAnchor}, ${country.name} may feel thinner than the countries that give you more medical-system confidence overall.`,
            `${country.name} can still work, but it is less convincing if ${healthcareAnchor}.`
          ], healthcareSeed),
          tags: ["healthcare", "stability"],
          score: 24,
        });
      }

      if (summary.need_reliable_internet && !highInternetCodes.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} may ask for more compromise because ${internetAnchor}, and the infrastructure case is less dependable than in your stronger connectivity options.`,
            `If ${internetAnchor}, ${country.name} looks riskier than the countries that give you a stronger connectivity baseline.`,
            `${country.name} can be harder to defend if ${internetAnchor}, because connectivity is more likely to become a practical tradeoff here.`
          ], `${workSeed}-internet-tradeoff`),
          tags: ["internet", "infrastructure"],
          score: 23,
        });
      }

      if (expatCommunityPriority && !strongExpatCommunityCodes.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} may feel thinner in the early transition if ${communityAnchor}, because the expat and newcomer layer is less established here.`,
            `If ${communityAnchor}, ${country.name} asks for more self-navigation than the countries with a stronger built-in support layer.`,
            `${country.name} can still work, but it offers less social cushioning up front if ${communityAnchor}.`
          ], communitySeed),
          tags: ["community", "integration"],
          score: 20,
        });
      }

      if (topPriorities.includes("safety") && !safetyPriorityCodes.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} may require more deliberate city or lifestyle choices if ${safetyAnchor}, because the day-to-day baseline is less even than in your steadier options.`,
            `If ${safetyAnchor}, ${country.name} introduces more variation than the countries that scored better on predictability.`,
            `${country.name} can still appeal, but it is harder to support cleanly when ${safetyAnchor}.`
          ], stabilitySeed),
          tags: ["safety", "stability"],
          score: 22,
        });
      }

      if (climateMismatch) {
        candidates.push({
          text: pickStableOption([
            `${country.name} may feel less natural if climate is part of the decision, because ${climateAnchor}.`,
            `If ${climateAnchor}, ${country.name} asks for more environmental compromise than the better-matched options in your shortlist.`,
            `${country.name} can still make sense overall, but it is not one of the stronger climate matches for the daily environment you described.`
          ], climateSeed),
          tags: ["climate", "lifestyle"],
          score: 21,
        });
      }

      if (summary.timeline === "asap" && country.visa_difficulty !== "Easy") {
        candidates.push({
          text: pickStableOption([
            `${country.name} asks for more patience than your timing suggests, because ${timelineAnchor} and the setup here takes longer than in the easier-entry options.`,
            `If ${timelineAnchor}, ${country.name} may feel slower or more admin-heavy than the countries that are simpler to get moving.`,
            `${country.name} is harder to support cleanly on timing because ${timelineAnchor}.`
          ], timelineSeed),
          tags: ["ease", "timeline"],
          score: 22,
        });
      }

      if (summary.work_abroad_intent === "local_employment" && !localEmploymentCodes.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} becomes harder to justify if ${localWorkAnchor}, because the practical case here is still stronger for lifestyle fit than for work-dependent relocation.`,
            `If ${localWorkAnchor}, ${country.name} asks for more optimism than the countries with a more straightforward employment case.`,
            `${country.name} can still appeal, but it is less straightforward if ${localWorkAnchor}.`
          ], `${workSeed}-local-tradeoff`),
          tags: ["career", "urban"],
          score: 21,
        });
      }

      if (summary.work_abroad_intent === "remote_self_employed" && !remoteFriendlyCodes.has(country.code)) {
        candidates.push({
          text: pickStableOption([
            `${country.name} asks for more setup work if ${remoteWorkAnchor}, because the move relies more on your own flexibility than on a simpler remote-work path.`,
            `If ${remoteWorkAnchor}, ${country.name} asks for more planning than the countries that support remote work more directly.`,
            `${country.name} falls behind on remote work because the day-to-day setup takes more work to maintain.`
          ], `${workSeed}-remote-tradeoff`),
          tags: ["remote", "ease"],
          score: 20,
        });
      }

      if (summary.language_flexibility === "english_only" && !country.language.includes("English")) {
        candidates.push({
          text: pickStableOption([
            `${country.name} asks for more day-to-day adaptation if you want English to carry more of the transition.`,
            `If you prefer to keep the move running comfortably in English, ${country.name} creates more friction than the easier-language alternatives.`,
            `${country.name} falls behind on language because English carries less of daily life here.`
          ], `${country.code}-language-${reasonType}`),
          tags: ["language", "integration"],
          score: 20,
        });
      }
    }

    return candidates;
  };

  const buildDynamicReasonCandidates = (country, reasonType = "fit") => {
    const priorities = summary.priorities || [];
    const candidates = [];
    const affordabilityPriority = priorities.includes("cost_of_living") || Boolean(summary.budget);
    const healthcarePriority = healthcareIsPrimaryDriver;
    const expatCommunityPriority = ["very_important", "important"].includes(summary.expat_community_importance);

    if (reasonType === "fit") {
      if (affordabilityPriority && country.cost_index <= budgetTarget - 12) {
        candidates.push({
          text: country.region === "Asia"
            ? `${country.name} gives you more room on day-to-day spending than almost any country in this set.`
            : `${country.name} gives you more room on day-to-day spending than most countries in this mix.`,
          tags: ["budget", "value"],
          score: 24,
        });
      } else if (affordabilityPriority && country.cost_index <= budgetTarget + 5) {
        candidates.push({
          text: country.region === "Europe"
            ? `${country.name} keeps costs controlled without forcing you into the cheapest path.`
            : country.region === "South America"
              ? `${country.name} keeps costs controlled without giving up basic stability.`
              : `${country.name} keeps costs reasonable without forcing a bargain-basement tradeoff.`,
          tags: ["budget", "value"],
          score: 18,
        });
      }

      if (healthcarePriority && country.healthcare_rating >= 4.4) {
        candidates.push({
          text: country.healthcare_rating >= 4.7
            ? `${country.name} is one of the clearest healthcare-first choices in this mix.`
            : country.region === "Europe"
              ? `${country.name} gives you a stronger healthcare case than many value-oriented alternatives nearby.`
              : `${country.name} gives you more confidence on healthcare depth than many lower-cost alternatives.`,
          tags: ["healthcare"],
          score: 22,
        });
      }

      if (summary.timeline === "asap" && country.visa_difficulty === "Easy") {
        candidates.push({
          text: `${country.name} is easier to justify when timing matters, because the path in is more straightforward than in more bureaucratic or slower-moving options.`,
          tags: ["ease"],
          score: 20,
        });
      }

      if (summary.work_abroad_intent === "remote_self_employed" && remoteFriendlyCodes.has(country.code)) {
        candidates.push({
          text: summary.need_reliable_internet
            ? `${country.name} stands up better for remote or self-employed work because internet reliability is stronger here.`
            : `${country.name} makes more sense for a flexible or remote profile because residency setup is simpler here.`,
          tags: summary.need_reliable_internet ? ["remote", "ease", "internet", "infrastructure"] : ["remote", "ease"],
          score: 20,
        });
      }

      if (summary.work_abroad_intent === "local_employment" && localEmploymentCodes.has(country.code)) {
        candidates.push({
          text: `${country.name} is easier to justify if local employment may matter, because the job market is deeper here.`,
          tags: ["career", "urban", "stability"],
          score: 18,
        });
      }

      if (summary.work_abroad_intent === "no" && passiveIncomeCodes.has(country.code)) {
        candidates.push({
          text: `${country.name} works better for a self-funded move because the plan does not depend on local employment.`,
          tags: ["ease", "value", "retiree"],
          score: 16,
        });
      }

      if (summary.need_reliable_internet && highInternetCodes.has(country.code)) {
        candidates.push({
          text: `${country.name} is a stronger practical fit if stable high-speed internet matters in daily life, because internet reliability is stronger here.`,
          tags: ["internet", "infrastructure"],
          score: 18,
        });
      }

      if (expatCommunityPriority && strongExpatCommunityCodes.has(country.code)) {
        candidates.push({
          text: `${country.name} offers a more established expat and newcomer support layer.`,
          tags: ["community", "integration"],
          score: 17,
        });
      }

      if (summary.intended_length_of_stay === "trial_exploratory_move" && country.visa_difficulty !== "Hard") {
        candidates.push({
          text: `${country.name} makes more sense for an exploratory move because it needs less long-horizon commitment than harder-entry options.`,
          tags: ["ease", "lifestyle"],
          score: 17,
        });
      }

      if (summary.intended_length_of_stay === "long_term_relocation" && longTermStrengthCodes.has(country.code)) {
        candidates.push({
          text: `${country.name} holds up better as a long-term relocation candidate because the systems base is stronger here.`,
          tags: ["stability", "healthcare", "lifestyle"],
          score: 17,
        });
      }
    } else {
      if (affordabilityPriority && country.cost_index >= budgetTarget + 10) {
        candidates.push({
          text: `${country.name} asks for tighter housing and everyday spending than the more budget-aligned countries in your results.`,
          tags: ["budget", "value"],
          score: 24,
        });
      }

      if (healthcarePriority && country.healthcare_rating < 4) {
        candidates.push({
          text: `${country.name} gives you a thinner healthcare margin than the options that scored better for system depth and medical access.`,
          tags: ["healthcare"],
          score: 20,
        });
      }

      if (country.visa_difficulty === "Hard" || (country.visa_difficulty === "Moderate" && summary.timeline === "asap")) {
        candidates.push({
          text: `${country.name} asks for a more deliberate transition plan because the move setup takes longer.`,
          tags: ["ease"],
          score: 18,
        });
      }

      if (summary.language_flexibility === "english_only" && !country.language.includes("English")) {
        candidates.push({
          text: `${country.name} creates more day-to-day friction if you want a move that can run comfortably in English for longer.`,
          tags: ["language", "integration"],
          score: 18,
        });
      }

      if (summary.work_abroad_intent === "local_employment" && !localEmploymentCodes.has(country.code)) {
        candidates.push({
          text: `${country.name} is harder to justify if local employment becomes necessary, because the job-market case is thinner here.`,
          tags: ["career", "tradeoff"],
          score: 18,
        });
      }

      if (summary.need_reliable_internet && !highInternetCodes.has(country.code)) {
        candidates.push({
          text: `${country.name} requires more compromise on connectivity consistency if dependable high-speed internet is part of your daily non-negotiables.`,
          tags: ["internet", "infrastructure"],
          score: 18,
        });
      }

      if (expatCommunityPriority && !strongExpatCommunityCodes.has(country.code)) {
        candidates.push({
          text: `${country.name} gives you less built-in newcomer support in the early transition.`,
          tags: ["community", "integration"],
          score: 16,
        });
      }

      if (summary.intended_length_of_stay === "trial_exploratory_move" && country.visa_difficulty === "Hard") {
        candidates.push({
          text: `${country.name} is less practical for a trial move because it asks for more commitment before you can validate fit on the ground.`,
          tags: ["ease", "lifestyle"],
          score: 18,
        });
      }
    }

    return candidates;
  };

  const buildNaturalLanguageReasonCandidates = (country, reasonType = "fit") => {
    if (!personalizationThemes.length) return [];

    const calmFriendlyCodes = new Set(["PT", "CR", "GR", "HR", "UY", "NZ", "IT"]);
    const healthcareStrongCodes = new Set(["ES", "FR", "DE", "AU", "JP", "SG", "CA", "NL"]);
    const budgetFriendlyCodes = new Set(["PT", "MX", "TH", "EC", "VN", "GR", "CO", "HR"]);
    const residencyFriendlyCodes = new Set(["PT", "MX", "PA", "CR", "GR"]);
    const urbanEnergyCodes = new Set(["ES", "MX", "TH", "CO", "FR", "DE", "JP", "SG", "NL", "AU"]);
    const familyFriendlyCodes = new Set(["PT", "ES", "CA", "NZ", "AU", "UY", "FR"]);
    const remoteFriendlyCodes = new Set(["PT", "MX", "TH", "MY", "ES", "NL", "SG"]);

    return personalizationThemes.flatMap((theme) => {
      if (reasonType === "fit") {
        if (theme.key === "calm" && calmFriendlyCodes.has(country.code)) {
          return [{ text: `${country.name} may feel stronger because you emphasized a calmer day-to-day lifestyle, and it generally supports that steadier rhythm better than more high-energy alternatives.`, tags: ["personalized", "lifestyle"], score: 19 }];
        }
        if (theme.key === "healthcare" && healthcareStrongCodes.has(country.code)) {
          return [{ text: `${country.name} stands out a little more here because you called out healthcare quality explicitly, and it tends to feel stronger on that front than many lifestyle-first options.`, tags: ["personalized", "healthcare"], score: 19 }];
        }
        if (theme.key === "budget" && budgetFriendlyCodes.has(country.code)) {
          return [{ text: `${country.name} earns extra explanatory weight because you emphasized manageable living costs, and it usually gives more day-to-day value than many higher-cost peers.`, tags: ["personalized", "budget"], score: 19 }];
        }
        if (theme.key === "residency" && residencyFriendlyCodes.has(country.code)) {
          return [{ text: `${country.name} may feel more practical because you emphasized a realistic residency pathway, and the transition here is generally easier to justify than in harder-entry options.`, tags: ["personalized", "ease"], score: 19 }];
        }
        if (theme.key === "city" && urbanEnergyCodes.has(country.code)) {
          return [{ text: `${country.name} may resonate more because you emphasized bigger-city energy, and it offers a stronger urban rhythm than the slower-paced countries in your shortlist.`, tags: ["personalized", "urban"], score: 18 }];
        }
        if (theme.key === "family" && familyFriendlyCodes.has(country.code)) {
          return [{ text: `${country.name} feels stronger in this presentation because you emphasized family life, and it often reads as a steadier long-term base for day-to-day living.`, tags: ["personalized", "family"], score: 18 }];
        }
        if (theme.key === "remote" && remoteFriendlyCodes.has(country.code)) {
          return [{ text: `${country.name} gets a little more emphasis because you highlighted remote-work flexibility, and it tends to support that lifestyle more naturally than many of the other options here.`, tags: ["personalized", "remote"], score: 18 }];
        }
      }

      if (theme.key === "healthcare" && !healthcareStrongCodes.has(country.code)) {
        return [{ text: `${country.name} may feel less convincing because you emphasized healthcare, and this is not one of the strongest healthcare-system leaders in your set.`, tags: ["personalized", "healthcare"], score: 19 }];
      }
      if (theme.key === "budget" && !budgetFriendlyCodes.has(country.code)) {
        return [{ text: `${country.name} may require more compromise because you emphasized manageable living costs, and it does not present as one of the strongest value plays in your shortlist.`, tags: ["personalized", "budget"], score: 19 }];
      }
      if (theme.key === "residency" && !residencyFriendlyCodes.has(country.code)) {
        return [{ text: `${country.name} may feel less straightforward because you emphasized a realistic residency pathway, and the move here still asks for a more deliberate setup.`, tags: ["personalized", "ease"], score: 18 }];
      }
      if (theme.key === "city" && !urbanEnergyCodes.has(country.code)) {
        return [{ text: `${country.name} may still appeal overall, but it can feel quieter or smaller-scale if bigger-city energy matters more to you.`, tags: ["personalized", "urban"], score: 18 }];
      }
      if (theme.key === "remote" && !remoteFriendlyCodes.has(country.code)) {
        return [{ text: `${country.name} may ask for more tradeoffs if remote-work flexibility is central, especially if dependable infrastructure is part of what you want day to day.`, tags: ["personalized", "remote"], score: 18 }];
      }

      return [];
    });
  };

  const buildSelectedComparisonCandidate = (country) => {
    if (!leadCardCountry) return null;

    if (country.cost_index >= (leadCardCountry.cost_index || budgetTarget) + 8) {
      return {
        text: `Compared with ${leadCardCountry.name}, it asks for noticeably more budget flexibility, which matters if ${budgetAnchor}.`,
        tags: ["comparison", "budget"],
        score: 40,
      };
    }

    if (country.healthcare_rating + 0.4 < (leadCardCountry.healthcare_rating || country.healthcare_rating)) {
      return {
        text: `Compared with ${leadCardCountry.name}, it gives you less margin on healthcare and day-to-day infrastructure confidence, which matters if ${healthcareAnchor}.`,
        tags: ["comparison", "healthcare"],
        score: 40,
      };
    }

    if (country.visa_difficulty === "Hard" && leadCardCountry.visa_difficulty !== "Hard") {
      return {
        text: `Compared with ${leadCardCountry.name}, the transition path looks less straightforward from a residency and setup standpoint, which matters if ${timelineAnchor}.`,
        tags: ["comparison", "ease"],
        score: 40,
      };
    }

    return {
      text: `Compared with ${leadCardCountry.name}, the bigger tradeoff is still city-level setup: housing, services, or routine logistics look less predictable once you narrow past the headline image.`,
      tags: ["comparison", "lifestyle"],
      score: 34,
    };
  };

  const deriveReasonKeys = (candidate = {}) => {
    const keyMap = {
      budget: "budget",
      value: "budget",
      healthcare: "healthcare",
      internet: "internet",
      infrastructure: "internet",
      remote: "remote",
      community: "community",
      integration: "community",
      safety: "safety",
      stability: "stability",
      climate: "climate",
      language: "language",
      culture: "culture",
      nature: "nature",
      career: "career",
      urban: "urban",
      ease: "ease",
      mobility: "ease",
      family: "family",
      retiree: "retiree",
      lifestyle: "lifestyle",
      comparison: "comparison"
    };

    const keys = Array.from(new Set((candidate.tags || []).map((tag) => keyMap[tag] || tag)));

    if (!keys.length) {
      if (/healthcare|medical/i.test(candidate.text || "")) keys.push("healthcare");
      if (/budget|cost|afford|value/i.test(candidate.text || "")) keys.push("budget");
      if (/internet|connectivity/i.test(candidate.text || "")) keys.push("internet");
      if (/community|expat|integration/i.test(candidate.text || "")) keys.push("community");
      if (/climate|warm|tropical|temperate/i.test(candidate.text || "")) keys.push("climate");
      if (/safety|stability|predictability/i.test(candidate.text || "")) keys.push("safety");
    }

    return keys.length ? keys : ["fallback"];
  };

  const deriveReasonGroups = (candidate = {}) => {
    const groups = Array.from(new Set((candidate.reasonKeys || candidate.tags || []).map((key) => REASON_GROUP_ALIASES[key] || key)));

    if (!groups.length) {
      const text = candidate.text || "";
      if (/budget|cost|afford|value/i.test(text)) groups.push("affordability");
      if (/healthcare|medical/i.test(text)) groups.push("healthcare");
      if (/internet|connectivity|infrastructure/i.test(text)) groups.push("connectivity");
      if (/community|expat|integration/i.test(text)) groups.push("community");
      if (/climate|warm|tropical|temperate/i.test(text)) groups.push("climate");
      if (/safety|stability|predictability/i.test(text)) groups.push("stability");
      if (/residency|visa|pathway|bureaucratic|entry/i.test(text)) groups.push("pathway");
      if (/language|english/i.test(text)) groups.push("language");
      if (/family|household|children/i.test(text)) groups.push("household");
      if (/remote|employment|work|job/i.test(text)) groups.push("work-setup");
    }

    return groups.length ? groups : ["fallback"];
  };

  const buildReasonSignature = (candidate = {}) => {
    const normalizedText = (candidate.text || "")
      .toLowerCase()
      .replace(/[’']/g, "")
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter((token) => token && !REASON_SIGNATURE_STOP_WORDS.has(token) && token.length > 2)
      .map((token) => {
        if (token.endsWith("ing") && token.length > 5) return token.slice(0, -3);
        if (token.endsWith("ed") && token.length > 4) return token.slice(0, -2);
        if (token.endsWith("es") && token.length > 4) return token.slice(0, -2);
        if (token.endsWith("s") && token.length > 4) return token.slice(0, -1);
        return token;
      });

    return {
      groups: deriveReasonGroups(candidate),
      tokens: Array.from(new Set(normalizedText)).slice(0, 10),
    };
  };

  const areReasonSignaturesTooSimilar = (signatureA, signatureB) => {
    if (!signatureA || !signatureB) return false;

    const sharedGroups = signatureA.groups.filter((group) => signatureB.groups.includes(group));
    const sharedTokens = signatureA.tokens.filter((token) => signatureB.tokens.includes(token));
    const smallestTokenCount = Math.max(1, Math.min(signatureA.tokens.length, signatureB.tokens.length));
    const tokenOverlap = sharedTokens.length / smallestTokenCount;

    if (sharedGroups.length >= 2) return true;
    if (sharedGroups.length >= 1 && tokenOverlap >= 0.5) return true;
    if (tokenOverlap >= 0.8) return true;

    return false;
  };

  const rankReasonCandidates = (candidates) => Array.from(
    new Map(candidates.filter(Boolean).map((candidate) => {
      const reasonKeys = deriveReasonKeys(candidate);
      const reasonGroups = deriveReasonGroups({ ...candidate, reasonKeys });
      const priorityBoost = reasonKeys
        .map((key) => reasonPriorityWeights.get(key) || 0)
        .sort((a, b) => b - a)
        .slice(0, 2)
        .reduce((total, value) => total + value, 0);

      return [candidate.text, {
        ...candidate,
        reasonKeys,
        reasonGroups,
        reasonSignature: buildReasonSignature({ ...candidate, reasonKeys, reasonGroups }),
        weightedScore: candidate.score + priorityBoost * 2,
      }];
    })).values()
  ).sort((a, b) => b.weightedScore - a.weightedScore || b.score - a.score);

  const selectDistinctReasons = (candidates, tracker, fallbackText, limit = 3, { blockedTags = new Set(), blockedPatterns = new Set() } = {}) => {
    const selected = [];
    const remaining = [...candidates];
    const localTags = new Map();
    const localGroups = new Map();
    const localPatterns = new Set();
    const localSignatures = [];

    tracker.tags = tracker.tags || new Map();
    tracker.groups = tracker.groups || new Map();
    tracker.patterns = tracker.patterns || new Set();
    tracker.signatures = tracker.signatures || [];

    const takeMatches = (predicate) => {
      for (let index = 0; index < remaining.length && selected.length < limit;) {
        const candidate = remaining[index];
        const pattern = normalizeReasonPattern(candidate.text);
        const reasonKeys = candidate.reasonKeys || candidate.tags;
        const reasonGroups = candidate.reasonGroups || reasonKeys;
        const semanticReasonKeys = reasonKeys.filter((tag) => !NON_DISTINCT_REASON_TAGS.has(tag));
        const semanticReasonGroups = reasonGroups.filter((group) => !NON_DISTINCT_REASON_TAGS.has(group));
        const effectiveReasonKeys = semanticReasonKeys.length ? semanticReasonKeys : reasonKeys;
        const effectiveReasonGroups = semanticReasonGroups.length ? semanticReasonGroups : reasonGroups;
        const hasLocalTag = effectiveReasonKeys.some((tag) => localTags.has(tag));
        const hasGlobalTag = effectiveReasonKeys.some((tag) => tracker.tags.has(tag));
        const hasUsedTag = hasLocalTag || hasGlobalTag;
        const hasLocalGroup = effectiveReasonGroups.some((group) => localGroups.has(group));
        const hasGlobalGroup = effectiveReasonGroups.some((group) => tracker.groups.has(group));
        const hasUsedGroup = hasLocalGroup || hasGlobalGroup;
        const isLocalPattern = localPatterns.has(pattern);
        const isGlobalPattern = tracker.patterns.has(pattern);
        const isRepeatedPattern = isLocalPattern || isGlobalPattern;
        const hasBlockedTag = effectiveReasonGroups.some((tag) => blockedTags.has(tag));
        const isBlockedPattern = blockedPatterns.has(pattern);
        const isNearDuplicate = localSignatures.some((signature) => areReasonSignaturesTooSimilar(candidate.reasonSignature, signature));
        const isGlobalNearDuplicate = tracker.signatures.some((signature) => areReasonSignaturesTooSimilar(candidate.reasonSignature, signature));
        const introducesFreshGroup = effectiveReasonGroups.some((group) => !localGroups.has(group) && !tracker.groups.has(group));
        const introducesFreshTag = effectiveReasonKeys.some((tag) => !localTags.has(tag) && !tracker.tags.has(tag));

        if (predicate({ candidate, hasUsedTag, hasLocalTag, hasGlobalTag, hasUsedGroup, hasLocalGroup, hasGlobalGroup, isRepeatedPattern, isLocalPattern, isGlobalPattern, hasBlockedTag, isBlockedPattern, isNearDuplicate, isGlobalNearDuplicate, introducesFreshGroup, introducesFreshTag })) {
          selected.push(candidate);
          effectiveReasonKeys.forEach((tag) => tracker.tags.set(tag, (tracker.tags.get(tag) || 0) + 1));
          effectiveReasonKeys.forEach((tag) => localTags.set(tag, (localTags.get(tag) || 0) + 1));
          effectiveReasonGroups.forEach((group) => tracker.groups.set(group, (tracker.groups.get(group) || 0) + 1));
          effectiveReasonGroups.forEach((group) => localGroups.set(group, (localGroups.get(group) || 0) + 1));
          tracker.patterns.add(pattern);
          localPatterns.add(pattern);
          tracker.signatures.push(candidate.reasonSignature);
          localSignatures.push(candidate.reasonSignature);
          remaining.splice(index, 1);
          continue;
        }

        index += 1;
      }
    };

    takeMatches(({ hasLocalGroup, hasGlobalGroup, isLocalPattern, isGlobalPattern, hasBlockedTag, isBlockedPattern, isNearDuplicate, isGlobalNearDuplicate }) => !hasLocalGroup && !hasGlobalGroup && !isLocalPattern && !isGlobalPattern && !hasBlockedTag && !isBlockedPattern && !isNearDuplicate && !isGlobalNearDuplicate);
    takeMatches(({ introducesFreshGroup, introducesFreshTag, isLocalPattern, isGlobalPattern, hasBlockedTag, isBlockedPattern, isNearDuplicate, isGlobalNearDuplicate }) => (introducesFreshGroup || introducesFreshTag) && !isLocalPattern && !isGlobalPattern && !hasBlockedTag && !isBlockedPattern && !isNearDuplicate && !isGlobalNearDuplicate);

    if (!selected.length) {
      const fallbackCandidate = {
        text: fallbackText(0),
        tags: ["fallback"],
        reasonKeys: ["fallback"],
        reasonGroups: ["fallback"],
        reasonSignature: { groups: ["fallback"], tokens: ["fallback"] },
        score: 0,
      };
      selected.push(fallbackCandidate);
    }

    return selected;
  };

  const extractDistinctReasonSignals = (candidates) => ({
    tags: new Set(
      candidates.flatMap((candidate) => (candidate.reasonGroups || candidate.reasonKeys || candidate.tags).filter((tag) => !NON_DISTINCT_REASON_TAGS.has(tag)))
    ),
    patterns: new Set(candidates.map((candidate) => normalizeReasonPattern(candidate.text))),
  });

  const priorityMetricMap = {
    cost_of_living: "cost_of_living_fit",
    healthcare: "healthcare_quality",
    safety: "safety_fit",
    taxes: "cost_of_living_fit",
    education: "healthcare_quality",
    community: "lifestyle_fit",
    career: "career_fit",
    culture: "lifestyle_fit",
    climate: "lifestyle_fit",
    nature: "lifestyle_fit",
    language_transition_ease: "language_accessibility",
    residency_pathway_practicality: "residency_path",
    infrastructure_daily_convenience: "infrastructure_fit",
  };

  const shortlistMetricConfig = {
    lifestyle_fit: { label: "lifestyle alignment", tags: ["lifestyle", "culture"] },
    healthcare_quality: { label: "healthcare confidence", tags: ["healthcare", "stability"] },
    safety_fit: { label: "safety stability", tags: ["safety", "stability"] },
    career_fit: { label: "career fit", tags: ["career", "urban"] },
    infrastructure_fit: { label: "infrastructure confidence", tags: ["infrastructure", "internet"] },
    language_accessibility: { label: "language accessibility", tags: ["language", "transition"] },
    residency_path: { label: "residency practicality", tags: ["ease", "mobility"] },
    cost_of_living_fit: { label: "long-term affordability", tags: ["budget", "value"] },
    ease_of_relocation: { label: "transition ease", tags: ["ease", "internet"] },
  };

  const buildShortlistMetricEntries = (country) => {
    const leadMetrics = leadCardCountry?.metrics || {};
    const countryMetrics = country?.metrics || {};

    return Object.entries(shortlistMetricConfig)
      .filter(([key]) => !suppressedExplanationMetricKeys.has(key))
      .map(([key, config]) => ({
      key,
      label: config.label,
      tags: config.tags,
      countryValue: Number(countryMetrics[key] || 0),
      leadValue: Number(leadMetrics[key] || 0),
      delta: Number(countryMetrics[key] || 0) - Number(leadMetrics[key] || 0),
      priorityBoost: Math.max(
        0,
        ...topPriorities
          .filter((priority) => priorityMetricMap[priority] === key)
          .map((priority) => Math.round((getPriorityRankWeight(priority) || 0) * 4))
      ),
    }));
  };

  const getCountryPriorityLeadLabels = (country, limit = 3) => {
    const metrics = country?.metrics || {};
    const labels = [];

    topPriorities.slice(0, 3).forEach((priority, index) => {
      const metricKey = priorityMetricMap[priority];
      const label = getInterpretationPriorityLabel(priority);
      if (!metricKey || !label || labels.includes(label)) return;

      const metricValue = Number(metrics[metricKey] || 0);
      const minimumScore = index === 0 ? 0 : index === 1 ? 72 : 76;
      if (index === 0 || metricValue >= minimumScore) {
        labels.push(label);
      }
    });

    topPriorities.slice(0, 3).forEach((priority) => {
      const label = getInterpretationPriorityLabel(priority);
      if (label && !labels.includes(label) && labels.length < limit) {
        labels.push(label);
      }
    });

    return labels.slice(0, limit);
  };

  const getCountryPracticalStabilizers = (country, excludedPriorityLabels = [], limit = 1) => {
    const excludedMetricKeys = new Set(
      topPriorities
        .slice(0, 3)
        .filter((priority) => excludedPriorityLabels.includes(getInterpretationPriorityLabel(priority)))
        .map((priority) => priorityMetricMap[priority])
        .filter(Boolean)
    );

    return Object.entries(shortlistMetricConfig)
      .filter(([metricKey]) => !excludedMetricKeys.has(metricKey) && !suppressedExplanationMetricKeys.has(metricKey))
      .map(([metricKey, config]) => ({ label: config.label, value: Number(country?.metrics?.[metricKey] || 0) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, limit)
      .map((entry) => entry.label);
  };

  const pickUnusedShortlistMetric = (entries, usedMetrics) => entries.find((entry) => !usedMetrics.has(entry.key));

  const buildShortlistFitText = (country, metricEntry, cardIndex) => {
    const leadName = decisionLeadCountry || leadCardCountry?.name || "the current lead";
    const priorityLeadLabels = getCountryPriorityLeadLabels(country, 2);
    const stabilizers = getCountryPracticalStabilizers(country, priorityLeadLabels, 1);

    if (priorityLeadLabels.length) {
      if (cardIndex === 1) {
        return `${country.name} looks especially strong on ${formatPriorityList(priorityLeadLabels)}.${stabilizers.length ? ` It also helps that ${formatPriorityList(stabilizers)} look solid here.` : ""}`;
      }
      if (cardIndex === 2) {
        return `${country.name} still looks strong on ${formatPriorityList(priorityLeadLabels)}.${stabilizers.length ? ` It also benefits from ${formatPriorityList(stabilizers)}.` : ""}`;
      }
      return `${country.name} still looks strong on ${formatPriorityList(priorityLeadLabels)}.${stabilizers.length ? ` It also benefits from ${formatPriorityList(stabilizers)}.` : ""}`;
    }

    if (metricEntry.delta > 0.75) {
      if (cardIndex === 1) {
        return `${country.name} looks especially strong on ${metricEntry.label}.`;
      }
      if (cardIndex === 2) {
        return `${country.name} still looks strong on ${metricEntry.label}.`;
      }
      return `${country.name} still looks strong on ${metricEntry.label}.`;
    }

    if (cardIndex === 1) {
      return `${country.name} looks strong on ${metricEntry.label}.`;
    }
    if (cardIndex === 2) {
      return `${country.name} looks strong on ${metricEntry.label}.`;
    }
    return `${country.name} looks strong on ${metricEntry.label}.`;
  };

  const buildShortlistTradeoffText = (country, metricEntry, cardIndex) => {
    const leadName = decisionLeadCountry || leadCardCountry?.name || "the current lead";
    const leadPriorityLabel = rankedInterpretationLabels[0] || metricEntry.label;

    if (metricEntry.delta < -0.75) {
      if (cardIndex === 1) {
        return `${country.name} gives up ground on ${leadPriorityLabel}.`;
      }
      if (cardIndex === 2) {
        return `${country.name} gives up ground on ${leadPriorityLabel}.`;
      }
      return `${country.name} asks for more compromise on ${leadPriorityLabel}.`;
    }

    if (cardIndex === 1) {
      return `${country.name} still looks thinner on ${leadPriorityLabel} than ${leadName}.`;
    }
    if (cardIndex === 2) {
      return `${country.name} still looks thinner on ${leadPriorityLabel}.`;
    }
    return `${country.name} still asks for more compromise on ${leadPriorityLabel}.`;
  };

  const buildShortlistFocusCandidate = (country, cardIndex, usedMetrics) => {
    const metricEntries = buildShortlistMetricEntries(country);
    const leadEdges = metricEntries
      .filter((entry) => entry.delta > 0.75)
      .sort((a, b) => (b.delta + b.priorityBoost) - (a.delta + a.priorityBoost));
    const overallStrengths = [...metricEntries]
      .sort((a, b) => (b.countryValue + b.priorityBoost) - (a.countryValue + a.priorityBoost));
    const selectedMetric = pickUnusedShortlistMetric(leadEdges, usedMetrics)
      || pickUnusedShortlistMetric(overallStrengths, usedMetrics)
      || leadEdges[0]
      || overallStrengths[0];

    if (!selectedMetric) return null;

    usedMetrics.add(selectedMetric.key);
    return {
      text: buildShortlistFitText(country, selectedMetric, cardIndex),
      tags: ["comparison", ...selectedMetric.tags],
      score: cardIndex === 1 ? 34 : 31,
    };
  };

  const buildShortlistTradeoffCandidate = (country, cardIndex, usedMetrics) => {
    const metricEntries = buildShortlistMetricEntries(country);
    const pressurePoints = metricEntries
      .filter((entry) => entry.delta < -0.75)
      .sort((a, b) => (Math.abs(b.delta) + b.priorityBoost) - (Math.abs(a.delta) + a.priorityBoost));
    const weakerDimensions = [...metricEntries]
      .sort((a, b) => (a.countryValue - b.countryValue) || (b.priorityBoost - a.priorityBoost));
    const selectedMetric = pickUnusedShortlistMetric(pressurePoints, usedMetrics)
      || pickUnusedShortlistMetric(weakerDimensions, usedMetrics)
      || pressurePoints[0]
      || weakerDimensions[0];

    if (!selectedMetric) return null;

    usedMetrics.add(selectedMetric.key);
    return {
      text: buildShortlistTradeoffText(country, selectedMetric, cardIndex),
      tags: ["comparison", ...selectedMetric.tags],
      score: cardIndex === 1 ? 30 : 27,
    };
  };

  const explanationMap = (() => {
    const summary = orientation?.summary || {};
    const fitTracker = { tags: new Map(), groups: new Map(), patterns: new Set(), signatures: [] };
    const tradeoffTracker = { tags: new Map(), groups: new Map(), patterns: new Set(), signatures: [] };
    const usedShortlistFitMetrics = new Set();
    const usedShortlistTradeoffMetrics = new Set();
    const visibleCards = visibleCountryCards;

    const buildLeadCountryFitCandidate = (country) => {
      if (country.name !== decisionLeadCountry) return null;

      const closestAlternative = orderedTopCountries.find((item) => item.name === decisionAlternativeCountry);
      const leadMetrics = country.metrics || {};
      const alternativeMetrics = closestAlternative?.metrics || {};
      const priorityLeadLabels = getCountryPriorityLeadLabels(country, 3);
      const stabilizers = getCountryPracticalStabilizers(country, priorityLeadLabels, 1);
      const comparisonCountry = closestAlternative?.name || "the closest alternative";
      const languageTail = topPriorities.includes("language_transition_ease")
        ? summary.language_flexibility === "english_only"
          ? " It also feels easier to navigate day to day in English."
          : " It also gives you a smoother early transition into daily life."
        : "";

      const text = priorityLeadLabels.length
        ? `${country.name} is strongest on ${priorityLeadLabels[0]}.${priorityLeadLabels.length > 1 ? ` It also holds up on ${formatPriorityList(priorityLeadLabels.slice(1, 2))}.` : ""}${stabilizers.length ? ` Also strong: ${formatPriorityList(stabilizers.slice(0, 1))}.` : ""}${languageTail}`
        : `${country.name} is the clearest overall fit right now.${languageTail}`;

      return {
        text,
        tags: ["lead-rationale", ...priorityLeadLabels.map((label) => label.toLowerCase())],
        score: 96,
      };
    };

    return visibleCards.reduce((acc, { country, cardType }) => {
      const cardIndex = orderedTopCountries.findIndex((item) => (item.code || item.name) === (country.code || country.name));
      const leadCountryFitCandidate = buildLeadCountryFitCandidate(country);
      const shortlistFitCandidate = cardType === "top" && cardIndex > 0
        ? buildShortlistFocusCandidate(country, cardIndex, usedShortlistFitMetrics)
        : null;
      const rawFitCandidates = [
        ...(shortlistFitCandidate ? [shortlistFitCandidate] : []),
        ...(leadCountryFitCandidate ? [leadCountryFitCandidate] : []),
        ...buildProfileAwareReasonCandidates(country, "fit"),
        ...buildNaturalLanguageReasonCandidates(country, "fit"),
        ...getRankedCountryReasonCandidates(country, summary, "fit"),
        ...buildDynamicReasonCandidates(country, "fit"),
      ];

      const reroutedTradeoffCandidates = rawFitCandidates.filter((candidate) => isCautionaryReason(candidate.text));
      const fitCandidates = rankReasonCandidates(rawFitCandidates.filter((candidate) => !isCautionaryReason(candidate.text)));

      const shortlistTradeoffCandidate = cardType === "top" && cardIndex > 0
        ? buildShortlistTradeoffCandidate(country, cardIndex, usedShortlistTradeoffMetrics)
        : null;

      const tradeoffCandidates = rankReasonCandidates([
        ...(shortlistTradeoffCandidate ? [shortlistTradeoffCandidate] : []),
        ...(cardType === "selected" ? [buildSelectedComparisonCandidate(country)] : []),
        ...reroutedTradeoffCandidates,
        ...buildProfileAwareReasonCandidates(country, "tradeoff"),
        ...buildNaturalLanguageReasonCandidates(country, "tradeoff"),
        ...getRankedCountryReasonCandidates(country, summary, "tradeoffs"),
        ...buildDynamicReasonCandidates(country, "tradeoff"),
      ]);

      const fitReasonCandidates = selectDistinctReasons(
        fitCandidates,
        fitTracker,
        () => buildFitFallback(country, summary)
      );

      const fitSignals = extractDistinctReasonSignals(fitReasonCandidates);

      const tradeoffReasonCandidates = selectDistinctReasons(
        tradeoffCandidates,
        tradeoffTracker,
        () => {
          if (cardType === "selected") {
            return buildSelectedComparisonCandidate(country)?.text || buildTradeoffFallback(country, summary);
          }
          return buildTradeoffFallback(country, summary);
        },
        3,
        {
          blockedTags: fitSignals.tags,
          blockedPatterns: fitSignals.patterns,
        }
      );

      const fitReasons = fitReasonCandidates
        .map((candidate) => formatReasonForSurface(candidate, { surface: "results", reasonType: "fit", country, index: cardIndex, countries: orderedTopCountries, summary }))
        .filter(Boolean);
      const tradeoffReasons = tradeoffReasonCandidates
        .map((candidate) => formatReasonForSurface(candidate, { surface: "results", reasonType: "tradeoff", country, index: cardIndex, countries: orderedTopCountries, summary }))
        .filter(Boolean);

      acc[country.code || country.name] = { fitReasons, tradeoffReasons };
      return acc;
    }, {});
  })();

  const renderCountryCard = (country, { showTopMatchBadge = false, variant = "primary", cardType = "top" } = {}) => {
    const countryKey = country.code || country.name;
    const regionLabel = countryRegions[country.name] || country.region || "Region";
    const bestRegions = bestRegionsByCountry[country.name] || fallbackRegions;
    const retireePresence = getUsRetireePresence(country);
    const focusLabels = dedupeOrderedLabels(country.focus_labels || []).slice(0, 2);
    const metrics = [
      {
        label: "Lifestyle Fit Score",
        value: displayScoreMap[countryKey] || `${clampNumber(Number(country.score) || 0, 0, 98.6).toFixed(1)}%`,
        icon: Sparkles,
        tooltip: "Shows how well this country aligns with the lifestyle preferences and priorities you shared in your assessment."
      },
      {
        label: "Cost Index",
        value: country.cost_index,
        icon: DollarSign,
        tooltip: "A relative cost read against typical U.S. expectations. Lower generally means more affordable.",
        detail: "Lower generally means more affordable."
      },
      {
        label: "Healthcare Confidence",
        value: country.healthcare_confidence_label || `${country.healthcare_rating}/5`,
        detail: country.healthcare_us_relative || undefined,
        icon: Heart,
        tooltip: "A short, practical read on how healthcare here is likely to feel relative to the U.S."
      },
      {
        label: "Primary Language",
        value: country.language,
        icon: Languages,
        tooltip: "Shows the primary language spoken in this country."
      },
      {
        label: "Residency Pathway",
        value: country.visa_difficulty,
        icon: Lock,
        tooltip: "Shows how workable a long-term stay or relocation path may be for your situation."
      },
      ...(isRetirementOrientedUser ? [{
        label: "U.S. retiree presence",
        value: retireePresence.tier,
        detail: retireePresence.description,
        icon: Users,
        tooltip: usRetireePresenceTooltip
      }] : [])
    ];
    const displayedMetrics = isLeanResultsPreview
      ? metrics
        .filter((metric) => LEAN_FREE_RESULTS_METRIC_LABELS.has(metric.label))
        .map((metric) => ({
          ...metric,
          label: metric.label === "Lifestyle Fit Score" ? "Overall Fit Score" : metric.label,
          detail: undefined,
        }))
      : metrics;
    const trustSignal = getCountryTrustSignal(country);
    const isSecondaryCard = variant === "secondary";
    const roleSummaryText = country.role_summary || "";
    const explanationEntry = explanationMap[countryKey] || {
      fitReasons: [buildFitFallback(country, summary)],
      tradeoffReasons: [cardType === "selected"
        ? buildSelectedComparisonCandidate(country)?.text || buildTradeoffFallback(country, summary)
        : buildTradeoffFallback(country, summary)],
    };
    const rawFitReasons = dedupeRenderedReasons(explanationEntry.fitReasons);
    const fitReasons = (() => {
      const filtered = rawFitReasons.filter((reason) => !roleSummaryText || !areReasonIdeasTooSimilar(reason, roleSummaryText));
      return filtered.length ? filtered : rawFitReasons;
    })();
    const hasGovernedTradeoffs = Array.isArray(country.processed_tradeoffs);
    const backendTradeoffReasons = hasGovernedTradeoffs
      ? country.processed_tradeoffs.filter(Boolean).slice(0, 3)
      : [];
    const rawTradeoffReasons = hasGovernedTradeoffs
      ? backendTradeoffReasons
      : dedupeRenderedReasons(explanationEntry.tradeoffReasons);
    const tradeoffReasons = hasGovernedTradeoffs
      ? rawTradeoffReasons
      : (() => {
        const filtered = rawTradeoffReasons
          .map((reason) => qualifyTradeoffAgainstFit(reason, fitReasons, roleSummaryText))
          .filter((reason) => !roleSummaryText || !areReasonIdeasTooSimilar(reason, roleSummaryText));
        return filtered.length ? filtered : rawTradeoffReasons.map((reason) => qualifyTradeoffAgainstFit(reason, fitReasons, roleSummaryText));
      })();
    const conciseCountryReason = polishLeanPreviewCopy(
      country.role_summary || fitReasons[0] || buildFitFallback(country, summary),
      country.name,
      "reason"
    );
    const rawWatchForTeaser = tradeoffReasons.find((reason) => typeof reason === "string" && reason.trim())
      || resultsWatchForFallbacks[country.code]
      || buildTradeoffFallback(country, summary);
    const watchForTeaser = polishLeanPreviewCopy(rawWatchForTeaser, country.name, "watch");
    const secondaryHeading = cardType === "top" ? "Tradeoffs to keep in mind" : "Why it may not fit";
    const fitHeading = cardType === "top" ? "Why it fits" : "Why it may fit";
    const defaultExpanded = cardType === "top" && showTopMatchBadge;
    const isExpanded = expandedCards[countryKey] ?? defaultExpanded;
    const disclosureLabel = isExpanded
      ? "Hide details"
      : cardType === "top"
        ? "See why it fits"
        : "View reasoning";

    const toggleCardDetails = () => {
      setExpandedCards((prev) => ({
        ...prev,
        [countryKey]: !(prev[countryKey] ?? defaultExpanded),
      }));
    };

    return (
      <Card
        key={countryKey}
        className={`overflow-hidden rounded-[2rem] border shadow-[0_24px_60px_rgba(28,25,23,0.08)] ${isSecondaryCard ? "border-stone-200/80 bg-stone-50/90" : "border-stone-200/80 bg-white/92"}`}
        data-testid={`country-card-${countryKey}`}
      >
        <div className={`relative overflow-hidden ${isSecondaryCard ? "bg-stone-50" : "bg-[linear-gradient(135deg,rgba(251,191,36,0.10),rgba(255,255,255,0.92),rgba(120,113,108,0.08))]"}`}>
          {showTopMatchBadge && (
            <div className="absolute top-4 left-4 z-10">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-100 px-3 py-1 text-amber-900" data-testid={`top-match-banner-${countryKey}`}>
                <span>🏆 Current lead</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center text-amber-800/75 transition-colors hover:text-amber-900"
                        data-testid={`top-match-banner-tooltip-trigger-${countryKey}`}
                        aria-label={`More information about Current lead for ${country.name}`}
                      >
                        <CircleHelp className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-[240px] text-xs text-white">The country currently leading this shortlist based on your priorities and assessment inputs.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          )}
          <div className="p-6 pt-10">
            <h3 className="text-2xl font-semibold text-stone-900" data-testid={`country-name-${countryKey}`}>
              <CountryLabel
                code={country.code}
                name={country.name}
                size={18}
                className="gap-2"
                flagClassName="mt-0.5"
                textClassName="leading-none font-manrope text-2xl"
                flagTestId={`country-flag-${countryKey}`}
              />
            </h3>
            <p className="mt-3 text-sm text-stone-500" data-testid={`country-region-${countryKey}`}>{regionLabel}</p>
            <p
              className="mt-1 max-w-[260px] truncate text-xs text-stone-500"
              title={trustSignal.label}
              data-testid={`country-review-signal-${countryKey}`}
            >
              {trustSignal.label}
            </p>
            <CountrySafetySignal
              safetyProfile={country.safety_profile}
              dataTestIdPrefix={`country-safety-signal-${countryKey}`}
            />
          </div>
        </div>
        <CardContent className="space-y-6 p-6 sm:p-8">
          {country.role_label || cardType === "selected" ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2" data-testid={`country-role-row-${countryKey}`}>
                {country.role_label ? (
                  <Badge
                    variant="outline"
                    className="rounded-full border-stone-200 bg-stone-50 text-stone-700"
                    data-testid={`country-role-label-${countryKey}`}
                  >
                    {country.role_label}
                  </Badge>
                ) : null}
                {cardType === "selected" ? (
                  <Badge
                    variant="outline"
                    className="rounded-full border-stone-300 bg-stone-100 text-stone-700"
                    data-testid={`country-selected-label-${countryKey}`}
                  >
                    Included by you
                  </Badge>
                ) : null}
              </div>
              {!isLeanResultsPreview && country.role_summary ? (
                <p className="text-sm leading-7 text-stone-700" data-testid={`country-role-summary-${countryKey}`}>
                  {country.role_summary}
                </p>
              ) : null}
              {!isLeanResultsPreview && paceSignalState.showCountryPaceDescriptions && cardType === "top" ? (
                <p className="text-xs leading-6 text-stone-500" data-testid={`country-pace-description-${countryKey}`}>
                  {`Daily pace: ${getCountryDailyPaceDescription(country.code)}`}
                </p>
              ) : null}
              {!isLeanResultsPreview && isRemoteRelevant(summary) ? (
                <p className="text-xs leading-6 text-stone-500" data-testid={`country-remote-compatibility-${countryKey}`}>
                  {`Remote work: ${getCountryRemoteCompatibilityLabel(country, summary)}`}
                </p>
              ) : null}
            </div>
          ) : null}

          <TooltipProvider>
            <div className={`grid gap-4 ${isLeanResultsPreview ? "sm:grid-cols-2" : "sm:grid-cols-2 xl:grid-cols-3"}`}>
              {displayedMetrics.map((metric) => {
                const Icon = metric.icon;
                const metricSlug = metric.label.toLowerCase().replace(/\s+/g, '-');

                return (
                  <div key={metric.label} className="rounded-[1.35rem] border border-stone-200/80 bg-stone-50/70 p-4" data-testid={`metric-${countryKey}-${metricSlug}`}>
                    <div className="flex items-start gap-3">
                    <Icon className="w-5 h-5 text-amber-700 mt-0.5" />
                    <div>
                      <div className="flex items-center gap-1.5">
                        <p className="text-xs text-stone-500">{metric.label}</p>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center text-stone-400 transition-colors hover:text-stone-900"
                              data-testid={`metric-tooltip-trigger-${countryKey}-${metricSlug}`}
                              aria-label={`More information about ${metric.label}`}
                            >
                              <CircleHelp className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-[240px] text-xs text-white">{metric.tooltip}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <p className="mt-2 font-semibold text-stone-900 text-sm">{metric.value}</p>
                      {metric.detail ? (
                        <p className="mt-1 text-xs leading-6 text-stone-500" data-testid={`metric-detail-${countryKey}-${metricSlug}`}>{metric.detail}</p>
                      ) : null}
                    </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </TooltipProvider>

          {isLeanResultsPreview ? (
            <div className="grid gap-4 md:grid-cols-2" data-testid={`country-preview-summary-${countryKey}`}>
              <div className="rounded-[1.2rem] border border-stone-200/80 bg-stone-50/70 p-4" data-testid={`country-reason-card-${countryKey}`}>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500" data-testid={`country-reason-label-${countryKey}`}>
                  Why it fits you
                </p>
                {focusLabels.length ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`country-focus-labels-${countryKey}`}>
                    <span className="text-[11px] uppercase tracking-[0.14em] text-stone-400">Reflects</span>
                    {focusLabels.map((label, index) => (
                      <span
                        key={`${countryKey}-focus-${label}`}
                        className="rounded-full border border-stone-200 bg-white px-2.5 py-1 text-[11px] font-medium text-stone-600"
                        data-testid={`country-focus-chip-${countryKey}-${index + 1}`}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                ) : null}
                <p className="mt-2 text-sm leading-7 text-stone-700" data-testid={`country-role-summary-${countryKey}`}>
                  {conciseCountryReason}
                </p>
              </div>
              <div className="rounded-[1.2rem] border border-amber-200/80 bg-amber-50/60 p-4" data-testid={`country-watch-for-card-${countryKey}`}>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500" data-testid={`watch-for-heading-${countryKey}`}>
                  Watch for
                </p>
                <p className="mt-2 text-sm leading-7 text-stone-700" data-testid={`country-watch-for-${countryKey}`}>
                  {watchForTeaser}
                </p>
              </div>
            </div>
          ) : null}

          {!isLeanResultsPreview && bestRegions.length ? (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4 text-amber-700" />
              <h4 className="text-sm font-semibold text-stone-900">Best Regions for You</h4>
            </div>
            <TooltipProvider>
              <div className="flex flex-wrap gap-2">
                {bestRegions.map((city) => (
                  <Tooltip key={city.name}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs text-stone-800 transition-[transform,border-color,background-color] duration-200 hover:-translate-y-0.5 hover:border-stone-300 hover:bg-white"
                        data-testid={`country-${countryKey}-city-${city.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {city.name}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs text-white">{city.info}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </TooltipProvider>
          </div>
          ) : null}

          {showExpandedCountryDetails ? (
          <div className="border-t border-stone-200/80 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={toggleCardDetails}
              className="rc-button-outline h-auto px-4 py-2 text-sm font-medium text-stone-900 hover:bg-stone-50"
              data-testid={`country-details-toggle-${countryKey}`}
              aria-expanded={isExpanded}
            >
              {disclosureLabel}
              {isExpanded ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />}
            </Button>
          </div>
          ) : null}

          {showExpandedCountryDetails && isExpanded ? (
            <>
                <div className={`grid gap-5 ${tradeoffReasons.length ? "md:grid-cols-2" : ""}`} data-testid={`country-details-content-${countryKey}`}>
                  <div>
                    <h4 className="mb-2 text-sm font-semibold text-stone-900">{fitHeading}</h4>
                    <ul className="list-disc list-inside space-y-2 text-sm leading-7 text-stone-600" data-testid={`fit-reasons-${countryKey}`}>
                      {fitReasons.map((reason, idx) => (
                        <li key={`${countryKey}-fit-${idx}`}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                  {tradeoffReasons.length ? (
                    <div>
                      <h4 className="mb-2 text-sm font-semibold text-stone-900">{secondaryHeading}</h4>
                      <ul className="list-disc list-inside space-y-2 text-sm leading-7 text-stone-600" data-testid={`not-fit-reasons-${countryKey}`}>
                        {tradeoffReasons.map((reason, idx) => (
                          <li key={`${countryKey}-caution-${idx}`}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              {trustSignal.warning ? (
                <p
                  className="text-xs text-stone-500"
                  data-testid={`country-review-warning-${countryKey}`}
                >
                  {trustSignal.warning}
                </p>
              ) : null}
            </>
          ) : null}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="rc-page-shell min-h-screen bg-background flex flex-col">
      <Header>
        <Button
          data-testid="share-results-button"
          variant="outline"
          onClick={handleShare}
          aria-label="Share Results"
          className="rounded-full border border-stone-700 bg-stone-900/80 px-3 sm:px-4 py-2 text-stone-300 transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-amber-400/45 hover:bg-stone-900 hover:text-stone-50 flex items-center gap-2"
        >
          <Share2 className="w-4 h-4" />
          <span className="hidden sm:inline">Share Results</span>
        </Button>
      </Header>

      <main className="rc-container py-8 md:py-12">
        <section className="mb-6 rc-panel p-8 text-left sm:p-10" data-testid="assessment-results-header">
          <div className="text-left">
            <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" data-testid="assessment-results-header-accent" />
            <p className="rc-section-kicker mb-3 text-left">Assessment Results</p>
            <h1 className="font-manrope mb-4 text-4xl text-left text-stone-900 md:text-5xl xl:whitespace-nowrap" data-testid="assessment-results-title">
              Your Assessment Results
            </h1>
          </div>
        </section>

        <section className="mb-10 rc-panel p-8 text-left sm:p-10" data-testid="results-generated-section">
          <p className="rc-section-kicker mb-2 text-left">Reading the shortlist</p>
          <h2 className="font-manrope mb-4 text-3xl text-left text-stone-900">How to Read These Results</h2>
          <div className="space-y-3 text-sm leading-7 text-stone-600">
            <p data-testid="results-generated-copy-1">
              This page is a concise preview of the countries that rose highest from your assessment.
            </p>
            <p data-testid="results-generated-copy-2">
              Each card shows why a country fits you based on the priorities and practical factors that stood out most in your assessment.
            </p>
            <p data-testid="results-generated-copy-3">
              The paid Relocation Blueprint is where the deeper analysis, tradeoffs, city and region guidance, residency detail, budget context, and practical next steps begin.
            </p>
          </div>
        </section>

        <section className="mb-12" data-testid="profile-summary-section">
          <p className="rc-section-kicker mb-2">User Profile</p>
          <div className="mb-5 flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-3xl">
              <h2 className="font-manrope text-3xl text-stone-900">Your Profile Summary</h2>
              <p className="mt-2 text-sm leading-7 text-stone-500" data-testid="profile-summary-helper-text">
                {personalizedProfileHelperText}
              </p>
              {visiblePayoffCopy ? (
                <p className="mt-3 max-w-2xl text-sm leading-7 text-stone-600" data-testid="profile-summary-payoff-visibility-copy">
                  {visiblePayoffCopy}
                </p>
              ) : null}
              {hasPaidPlan ? (
                <p className="mt-3 max-w-2xl text-sm leading-7 text-stone-600" data-testid="results-plan-refresh-helper-copy">
                  {planAccess.requires_new_purchase_for_refresh
                    ? "You have already used your included Blueprint refresh. Updating your assessment again will require a new paid Blueprint if you want refreshed results and guidance."
                    : "Your paid Relocation Blueprint includes one free refresh. If you update your assessment, we’ll generate a refreshed Blueprint at no extra charge."}
                </p>
              ) : null}
              <button
                type="button"
                onClick={() => setIsProfileSummaryExpanded((prev) => !prev)}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-stone-500 transition-colors hover:text-stone-900"
                data-testid="profile-summary-toggle"
                aria-expanded={isProfileSummaryExpanded}
              >
                {isProfileSummaryExpanded ? "Hide details" : "Show details"}
                {isProfileSummaryExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
            </div>
            <Button
              asChild
              variant="ghost"
              className="rc-button-outline h-10 px-4"
              data-testid="update-assessment-link"
            >
              <Link to="/assessment?step=review" data-testid="update-assessment-link-target">
                Update Assessment
              </Link>
            </Button>
          </div>
          {summarizedNaturalLanguageIntro ? (
            <div className="mb-5 rounded-[1.4rem] border border-stone-200/80 bg-stone-50/75 p-4 sm:p-5" data-testid="natural-language-intro-results-section">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">What You Shared</p>
              <p className="mt-2.5 text-sm leading-7 text-stone-800" data-testid="natural-language-intro-results-text">
                {summarizedNaturalLanguageIntro}
              </p>
              <p className="mt-1.5 text-xs leading-6 text-stone-500" data-testid="natural-language-intro-results-supporting-text">
                Used only to lightly personalize how your results are explained.
              </p>
            </div>
          ) : null}
          <Card className="rounded-[2rem] border border-stone-200/80 bg-white/90 shadow-[0_18px_45px_rgba(28,25,23,0.06)]">
            <CardContent className="p-6 sm:p-8">
              {!isProfileSummaryExpanded ? (
                <div className="space-y-4" data-testid="profile-summary-collapsed-content">
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="profile-summary-collapsed-snapshot">
                    {compactProfileSnapshot.map((item) => (
                      <div
                        key={item.label}
                        className="rounded-[1.35rem] border border-stone-200/80 bg-stone-50/70 px-4 py-3"
                        data-testid={item.testId}
                      >
                        <p className="text-[11px] uppercase tracking-wide text-stone-500 mb-1">{item.label}</p>
                        <p className="text-sm font-medium text-stone-900 leading-snug">{item.value}</p>
                      </div>
                    ))}
                  </div>
                  {payoffVisibilityChips.length ? (
                    <div className="flex flex-wrap items-center gap-2" data-testid="profile-summary-collapsed-payoff-chip-row">
                      <span className="text-[11px] uppercase tracking-[0.14em] text-stone-400">Also reflected</span>
                      {payoffVisibilityChips.map((label, index) => (
                        <span
                          key={`collapsed-payoff-${label}`}
                          className="rounded-full border border-stone-200 bg-white px-2.5 py-1 text-[11px] font-medium text-stone-600"
                          data-testid={`profile-summary-collapsed-payoff-chip-${index + 1}`}
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <p className="text-xs text-stone-500" data-testid="profile-summary-collapsed-helper-text">
                    Expand to see the full profile details.
                  </p>
                </div>
              ) : (
                <div className="space-y-6" data-testid="profile-summary-expanded-content">
                  <div className="grid lg:grid-cols-2 gap-10 lg:gap-12">
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-3">Top Priorities</h3>
                      <ol className="list-decimal list-inside space-y-2 text-sm leading-7 text-stone-600" data-testid="top-priorities-list">
                        {topPriorities.length ? (
                          formattedTopPriorities.map((priority) => (
                            <li key={priority}>{priority}</li>
                          ))
                        ) : (
                          <li>Not specified</li>
                        )}
                      </ol>
                    </div>
                    <div className="lg:border-l lg:border-stone-200/80 lg:pl-8">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-3">Core Parameters</h3>
                      <dl className="space-y-2 text-sm">
                        {coreParameters.map((item) => (
                          <div key={item.label} className="grid grid-cols-[minmax(0,9.5rem)_minmax(0,1fr)] items-baseline gap-x-3" data-testid={`core-parameter-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                            <dt className="text-stone-500">{item.label}</dt>
                            <dd className="text-stone-900 font-medium">{item.value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-3">Key Lifestyle Preferences</h3>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {keyPreferences.map((pref) => (
                        <div key={pref.label} className="rounded-[1.35rem] border border-stone-200/80 bg-stone-50/70 p-4" data-testid={`lifestyle-pref-${pref.label.toLowerCase().replace(/\s+/g, '-')}`}>
                          <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">{pref.label}</p>
                          <p className="text-sm font-medium text-stone-900">{pref.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  {(topMotivationLabels.length || topLifestyleRefinerLabels.length) ? (
                    <div className="grid gap-6 lg:grid-cols-2" data-testid="profile-summary-recognized-inputs-grid">
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-3">What this says about the move you are trying to make</h3>
                        <div className="flex flex-wrap gap-2" data-testid="profile-summary-motivation-chips">
                          {topMotivationLabels.length ? topMotivationLabels.map((label, index) => (
                            <span
                              key={`motivation-chip-${label}`}
                              className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1.5 text-xs font-medium text-stone-700"
                              data-testid={`profile-summary-motivation-chip-${index + 1}`}
                            >
                              {label}
                            </span>
                          )) : (
                            <p className="text-sm leading-7 text-stone-500" data-testid="profile-summary-motivation-empty-state">No motivations selected.</p>
                          )}
                        </div>
                        <p className="mt-3 text-xs leading-6 text-stone-500" data-testid="profile-summary-motivation-helper-text">
                          These motivations do not outweigh your core priorities. They help explain the kind of move you are trying to make and why certain tradeoffs receive more emphasis in the shortlist.
                        </p>
                      </div>
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-3">Lifestyle refiners we are using</h3>
                        <div className="flex flex-wrap gap-2" data-testid="profile-summary-lifestyle-refiner-chips">
                          {topLifestyleRefinerLabels.length ? topLifestyleRefinerLabels.map((label, index) => (
                            <span
                              key={`lifestyle-refiner-chip-${label}`}
                              className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1.5 text-xs font-medium text-stone-700"
                              data-testid={`profile-summary-lifestyle-refiner-chip-${index + 1}`}
                            >
                              {label}
                            </span>
                          )) : (
                            <p className="text-sm leading-7 text-stone-500" data-testid="profile-summary-lifestyle-refiner-empty-state">No lifestyle refiners selected.</p>
                          )}
                        </div>
                        <p className="mt-3 text-xs leading-6 text-stone-500" data-testid="profile-summary-lifestyle-refiner-helper-text">
                          These do not outweigh your main priorities. They help separate close options and add more day-to-day detail to the country reasoning.
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {showBudgetRealityCheck ? (
        <section className="mb-12 rounded-[2rem] border border-stone-300/90 bg-white/90 p-6 shadow-[0_18px_45px_rgba(28,25,23,0.06)] sm:p-7" data-testid="current-life-comparison-module">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-2xl">
              <h3 className="mt-2 font-manrope text-2xl text-stone-900" data-testid="current-life-comparison-title">
                Budget Reality Check
              </h3>
              <p className="mt-2 text-sm leading-7 text-stone-600" data-testid="current-life-comparison-helper-text">
                See how your budget aligns with your top country fits.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCurrentLifeModuleExpanded((current) => !current)}
              data-testid="current-life-comparison-toggle"
              className="rc-button-outline h-10 px-4"
            >
              {isCurrentLifeModuleExpanded ? "Hide" : "Review"}
            </Button>
          </div>

          {isCurrentLifeModuleExpanded ? (
            <div className="mt-5 space-y-5" data-testid="current-life-comparison-expanded">
              {relocationBudgetDetails ? (
                <div className="space-y-4" data-testid="budget-reality-check-content">
                  <div className="rounded-[1.4rem] border border-stone-200/80 bg-stone-50/75 px-4 py-4 sm:px-5" data-testid="budget-selected-budget-card">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500" data-testid="budget-selected-budget-label">
                      Your Selected Relocation Budget
                    </p>
                    <p className="mt-2 text-sm font-medium leading-7 text-stone-900" data-testid="budget-selected-budget-value">
                      {relocationBudgetDetails.label}
                    </p>
                  </div>

                  <div className="overflow-hidden rounded-[1.4rem] border border-stone-200/80 bg-white" data-testid="budget-fit-table-wrapper">
                    <div className="grid grid-cols-[1.1fr_1.1fr_0.8fr] gap-3 border-b border-stone-200/80 bg-stone-50/85 px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-stone-500 sm:px-5" data-testid="budget-fit-table-header">
                      <p data-testid="budget-fit-header-country">Country</p>
                      <p data-testid="budget-fit-header-range">Typical monthly range</p>
                      <p data-testid="budget-fit-header-label">Budget fit</p>
                    </div>
                    <div className="divide-y divide-stone-200/80" data-testid="budget-fit-table-body">
                      {budgetRealityCheckRows.map((row) => (
                        <div
                          key={row.code}
                          className="grid grid-cols-1 gap-3 px-4 py-4 sm:grid-cols-[1.1fr_1.1fr_0.8fr] sm:px-5"
                          data-testid={`budget-fit-row-${row.code}`}
                        >
                          <p className="text-sm font-medium text-stone-900" data-testid={`budget-fit-country-${row.code}`}>{row.name}</p>
                          <p className="text-sm text-stone-700" data-testid={`budget-fit-range-${row.code}`}>{row.range}</p>
                          <div>
                            <span
                              className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${BUDGET_FIT_TONE_STYLES[row.fitLabel] || BUDGET_FIT_TONE_STYLES.Workable}`}
                              data-testid={`budget-fit-label-${row.code}`}
                            >
                              {row.fitLabel}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {budgetRealityCheckInterpretationBullets.length ? (
                    <div className="rounded-[1.4rem] border border-stone-200/80 bg-white px-4 py-4 sm:px-5" data-testid="budget-reality-check-interpretation">
                      <ul className="space-y-3 text-sm leading-7 text-stone-700" data-testid="budget-reality-check-interpretation-list">
                        {budgetRealityCheckInterpretationBullets.map((bullet, index) => (
                          <li
                            key={bullet.code}
                            className={`flex items-start gap-3 rounded-[1rem] border px-3 py-3 ${index === 0 ? "border-emerald-200 bg-emerald-50/60" : index === budgetRealityCheckInterpretationBullets.length - 1 ? "border-amber-200 bg-amber-50/60" : "border-stone-200 bg-stone-50/50"}`}
                            data-testid={`budget-reality-check-interpretation-${index + 1}`}
                          >
                            <span className="mt-[10px] h-1.5 w-1.5 rounded-full bg-stone-400" aria-hidden="true" />
                            <span>
                              <span className="font-medium text-stone-900">{bullet.name}</span>
                              <span className="text-stone-500"> — </span>
                              {bullet.text}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
        ) : null}


        {showResultsMeaningSection ? (
        <section className="mb-12 max-w-4xl border-t border-stone-200/80 pt-8" data-testid="results-meaning-section">
          <p className="rc-section-kicker mb-2">Recommendations</p>
          <h2 className="font-manrope text-3xl text-stone-900 mb-4">What These Results Mean</h2>
          <div className="space-y-3 text-sm leading-7 text-stone-600">
            {resultsMeaningLines.map((line, index) => (
              <p
                key={`results-meaning-line-${index}`}
                className={index === 1 ? "text-stone-700" : "text-stone-600"}
                data-testid={[
                  "results-meaning-decision-signal",
                  "results-meaning-interpretation-focus",
                  "results-meaning-support-priority",
                ][index] || `results-meaning-line-${index + 1}`}
              >
                {line}
              </p>
            ))}
          </div>
        </section>
        ) : null}

        <section className="mb-8" data-testid="countries-section-header">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-3xl">
              <h2 className="font-manrope text-3xl text-stone-900">Your Top Country Fits</h2>
              <p className="mt-2 text-sm leading-7 text-stone-500" data-testid="country-matches-subtitle">
                {isLeanResultsPreview
                  ? "A concise first read on the countries most worth exploring further, based on what mattered most in your assessment."
                  : decisionLeadCountry && decisionAlternativeCountry
                  ? `${decisionLeadCountry} leads, and ${decisionAlternativeCountry} stays closest. The cards below show each country’s clearest strengths and tradeoffs.`
                  : "Your strongest shortlist based on the assessment you completed."}
              </p>
              {isLeanResultsPreview && recommendationTraceabilityLabels.length ? (
                <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="results-priority-traceability-row">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-stone-400">Most visible in your recommendations</span>
                  {recommendationTraceabilityLabels.map((label, index) => (
                    <span
                      key={`results-traceability-${label}`}
                      className="rounded-full border border-stone-200 bg-stone-50 px-2.5 py-1 text-[11px] font-medium text-stone-600"
                      data-testid={`results-priority-traceability-chip-${index + 1}`}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              ) : null}
              {isLeanResultsPreview && hasSelectedLifestyleRefiners ? (
                <p className="mt-3 text-xs leading-6 text-stone-500" data-testid="results-lifestyle-refiner-acknowledgement">
                  Lifestyle refiners like {formatPriorityList(topLifestyleRefinerLabels.slice(0, 3))} are helping separate the closer calls in this shortlist, rather than driving the ranking on their own.
                </p>
              ) : null}
            </div>
          </div>
        </section>

        <section className="mb-14 space-y-6" data-testid="country-cards-section">
          {topRankedCountryCards.length ? (
            topRankedCountryCards.map(({ country, cardType }) => renderCountryCard(country, {
              showTopMatchBadge: decisionLeadCountry
                ? decisionLeadCountry === country.name
                : false,
              variant: "primary",
              cardType,
            }))
          ) : (
            <p className="text-sm text-muted-foreground">No results available yet.</p>
          )}
        </section>

        {selectedCountryCards.length ? (
          <>
            <section className="mb-8" data-testid="selected-countries-section-header">
              <div className="max-w-3xl">
                <h2 className="font-manrope text-3xl text-stone-900">Included Based on Your Selection</h2>
                <p className="mt-2 text-sm leading-7 text-stone-500" data-testid="selected-countries-subtitle">
                  These countries are still visible because you asked to keep them in view, so you can compare them against your strongest fits using the same priorities and practical factors.
                </p>
              </div>
            </section>

            <section className="mb-14 space-y-6" data-testid="selected-country-cards-section">
              {selectedCountryCards.map(({ country, cardType }) => renderCountryCard(country, {
                showTopMatchBadge: false,
                variant: "secondary",
                cardType,
              }))}
            </section>
          </>
        ) : null}

        <section className="mb-10 rounded-[2rem] border border-stone-200 bg-stone-900 p-8 text-stone-50 shadow-[0_30px_70px_rgba(28,25,23,0.18)]" data-testid="relocation-plan-cta">
          <p className="text-xs uppercase tracking-wide text-stone-300 mb-2">Relocation Blueprint</p>
          <h2 className="font-manrope text-3xl text-stone-50 mb-2" data-testid="relocation-plan-cta-title">
            You have your shortlist. Your Relocation Blueprint is ready.
          </h2>
          <p className="text-stone-300" data-testid="relocation-plan-cta-copy">
            Your free results narrowed the field. Your personalized Relocation Blueprint helps you compare your strongest options, understand the tradeoffs, and decide which country is the best fit for your life abroad.
          </p>
          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-2 text-sm text-stone-300">
              {[
                "City and region guidance",
                "Deeper tradeoffs and practical constraints",
                "Residency pathway detail and comparison context",
                "Budget context and next-step planning"
              ].map((item) => (
                <div key={item} className="flex items-start gap-2" data-testid={`relocation-plan-cta-feature-${item.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <CheckCircle className="w-4 h-4 text-amber-400 mt-0.5" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col items-start gap-3">
              {hasPaidPlan && !planAccess.requires_new_purchase_for_refresh ? (
                <Button asChild size="lg" data-testid="view-plan-btn" className="rc-button-accent flex items-center gap-2 px-6 py-3">
                  <Link to="/plan">
                    View My Full Relocation Blueprint
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              ) : (
                <Button onClick={handleUpgrade} size="lg" data-testid="unlock-plan-btn" className="rc-button-accent flex items-center gap-2 px-6 py-3">
                  {hasPaidPlan ? "Refresh My Full Relocation Blueprint — $29" : "Unlock My Full Relocation Blueprint — $29"}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              )}
              <div className="text-sm text-stone-300" data-testid="relocation-plan-cta-supporting-copy">
                <p>{hasPaidPlan && !planAccess.requires_new_purchase_for_refresh ? "Your current Blueprint already includes the deeper guidance." : "$29 — one-time purchase"}</p>
                <p>
                  {hasPaidPlan
                    ? planAccess.requires_new_purchase_for_refresh
                      ? "A new purchase is required to refresh the full Blueprint with your latest answers."
                      : "Review your current full Blueprint any time."
                    : "The shortlist stays free. The full Blueprint is where the deeper guidance begins."}
                </p>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
      <PlanAvailabilityDialog open={showPlanAvailabilityDialog} onOpenChange={setShowPlanAvailabilityDialog} />
    </div>
  );
};

export default Orientation;
